import { useEffect, useMemo, useRef, useState } from 'react'
import { TITLE_BLUE, site } from '../data/site.js'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo.jsx'
import MobileExpandableDescription from '../components/MobileExpandableDescription.jsx'
import AnalyticsHeroVisual from '../components/AnalyticsHeroVisual.jsx'
import { Code2, Smartphone, Sparkles, Cpu } from 'lucide-react'
import manojImg from '../assets/manoj.webp'
import snehaImg from '../assets/sneha.webp'
import ExperienceSection from '../components/ExperienceSection.jsx'
import ValueCarousel, { AdlcCostBand } from '../components/ValueCarousel.jsx'
import ReviewsSection from '../sections/ReviewsSection.jsx'
import { showcaseImage } from '../lib/showcaseImage.js'
import CountryTag from '../components/CountryTag.jsx'
import ContactSection from '../sections/ContactSection.jsx'
import WorkflowGrid, { DEFAULT_PHASES } from '../sections/WorkflowGrid.jsx'
import { useInView } from '../hooks/useInView.js'
import { useCountUp } from '../hooks/useCountUp.js'

// Custom Premium Assets
import homepageImg1 from '../assets/Homepage/2.jpg'
import homepageImg2 from '../assets/Homepage/3.png'
import homepageImg3 from '../assets/Homepage/4.png'
import homepageImg4 from '../assets/Homepage/5.png'
import homepageImg5 from '../assets/Homepage/6.png'
import homepageImg6 from '../assets/Homepage/9.png'
import homepageImg8 from '../assets/Homepage/7.png'
import { fallbackContent, fetchWithFallback } from '../lib/contentFallback.js'

// ============================================================
// The home page is a full React/JSX rebuild of the original
// NextSaaS template body. It still relies on `main.css` for the
// custom utility classes (badge, btn, text-heading-*, etc.) and
// loads the template's GSAP/Swiper bundle for entrance + carousel
// animations — but every section below is real JSX, no innerHTML.
// ============================================================

const CSS = [
  '/legacy/assets/main.css',
  '/legacy/assets/custom.css',
]
// The legacy template's JS bundle (GSAP + ScrollTrigger + Lenis + Swiper +
// main.js — ~1MB total) was blocking the main thread for several seconds on
// some devices, freezing the page. None of it is essential: animations are
// React-driven and the Swiper carousels show static slides anyway. Disabled.
// Force `[data-ns-animate]` opacity:1 so content stays visible even if GSAP
// doesn't initialise; appended last so it wins the cascade against main.css.
const INLINE_CSS = `
  [data-ns-animate]{ opacity: 1 !important; }
  .header-one{ opacity: 1 !important; }
  .swiper-button-next, .swiper-button-prev{ color: #020d23; }
`

/* ---------------- data ---------------- */

/* The four capability marks under the hero paragraph. Data rather than four
   near-identical blocks of markup. */
/* What we build. The track record — years, clients, rating, countries,
   on-time delivery — is the job of the section directly below the hero, so
   none of it appears up here: two sections making the same claim twice read
   as filler, and the visitor learns nothing new by scrolling. */
/* The two people a new enquiry actually reaches. */
const FOUNDERS = [
  { name: 'Manoj Koravangi', role: 'Founder', img: manojImg },
  { name: 'Sneha', role: 'Co-founder', img: snehaImg },
];

const HERO_CAPABILITIES = [
  { label: 'Websites & web apps', Icon: Code2 },
  { label: 'iOS & Android apps', Icon: Smartphone },
  { label: 'AI & automation', Icon: Sparkles },
  { label: 'IoT, drones & hardware', Icon: Cpu },
];

// Rotating snippets the AI "types" in the animated hero card.
const CODE_LINES = [
  'spectranox.ai.build({',
  '  framework: "react",',
  '  speed: "instant"',
  '});',
  '> Generating components ✓',
  '> Running tests... ✓',
  '> Deploying v2.4.1 ✓',
]

// Rotating tasks the AI shows as "currently running".
const AI_TASKS = [
  'Generating UI components',
  'Refactoring legacy code',
  'Optimizing performance',
  'Running test suite',
  'Deploying to production',
  'Monitoring live metrics',
]

const SERVICES_CARDS = [
  { title: 'Website Development', text: 'Cryptocurrency lending is a financial service that allows individuals to lend', img: '/legacy/images/new/web-development.png', delay: '0.3' },
  { title: 'App Development', text: 'Payment solutions play a vital role in streamlining financial transactions', img: '/legacy/images/new/app-dev-2.png', delay: '0.5' },
]

// Nine services for the "Services We Offer" masonry. Aspect ratios are
// deliberately mixed (16/9 / 4/3 / 5/4 / 3/4) so cards have different heights
// and stagger naturally inside CSS-column-based masonry.
const SERVICES_GRID = [
  { slug: 'web', title: 'Website Development', desc: 'Lightning-fast corporate sites, e-commerce stores and customer portals on modern stacks. SEO-ready and engineered for real conversions.', img: '/legacy/images/new/web-development.png', tag: 'WEB', accent: '#67e8f9', aspect: '16 / 9' },
  { slug: 'app', title: 'App Development', desc: 'Native iOS, Android and cross-platform apps with polished UX and rock-solid performance. We design, build, ship, and maintain.', img: '/legacy/images/new/app-dev-2.png', tag: 'APP', accent: '#a78bfa', aspect: '4 / 5' },
  { slug: 'cyber', title: 'Cyber Security', desc: 'Pen-testing, audits, hardening and 24/7 SIEM/SOC operations. Find vulnerabilities first, stop threats fast, and stay compliant.', img: '/legacy/images/new/cyber.png', tag: 'SEC', accent: '#fb9a8f', aspect: '5 / 4' },
  { slug: 'ai', title: 'AI Development', desc: 'Custom LLM apps, RAG systems and intelligent automation. Embed AI into your product or operations with measurable impact.', img: '/legacy/images/new/aipowered.png', tag: 'AI', accent: '#f472b6', aspect: '3 / 4' },
  { slug: 'social', title: 'Social Media Management', desc: 'End-to-end social presence: content calendars, community, paid social and analytics. Build engaged audiences across every platform.', img: '/legacy/images/new/smm.png', tag: 'SMM', variant: 'accent', aspect: '16 / 11' },
  { slug: 'marketing', title: 'Digital Marketing', desc: 'SEO, paid campaigns and content strategy that drive measurable growth. Data-driven optimisation with transparent ROI reporting.', img: '/legacy/images/new/dm.png', tag: 'DM', accent: '#fbbf24', aspect: '4 / 3' },
  { slug: 'uiux', title: 'UI / UX Design', desc: 'Research-led product design, prototypes and design systems. Interfaces that delight users and drive measurable business outcomes.', img: '/legacy/images/new/ui.png', tag: 'UX', accent: '#38bdf8', aspect: '16 / 9' },
  { slug: 'data', title: 'Data Analytics', desc: 'BI dashboards, ETL pipelines and predictive models. Turn raw data into decisions that move your business forward, fast.', img: '/legacy/images/webdev.png', tag: 'DATA', accent: '#818cf8', aspect: '4 / 5' },
  { slug: 'consulting', title: 'Business Consulting', desc: 'Strategy, process optimisation and technology roadmaps for sustainable growth. Better decisions, backed by data and experience.', img: '/legacy/images/team.jpg', tag: 'BIZ', accent: '#34d399', aspect: '16 / 10' },
]

// Compact horizontal cards used by the live "Services We Offer" masonry.
// Widths rhythm: 5/4/3 → 3/4/5 (12-col grid) creates the masonry feel while
// keeping uniform row heights. SMM is the lime-green accent card.
const SPX_SERVICES = [
  /* Priority order, same as the nav. The col spans still sum to 12 per
     row, so the masonry rhythm survives the reorder: 5+4+3, then 4+5+3. */
  { key: 'web', tag: 'WEB', title: 'Website Development', desc: 'Lightning-fast corporate sites, e-commerce and customer portals on modern stacks. SEO-ready, accessible, and engineered for real conversions.', image: '/legacy/images/new/web-development.png', accent: '#38bdf8', col: 'lg:col-span-5', variant: 'dark' },
  { key: 'app', tag: 'APP', title: 'App Development', desc: 'Native iOS, Android and cross-platform apps with polished UX and rock-solid performance. We design, build, ship, and maintain.', image: '/legacy/images/new/app-dev-2.png', accent: '#a855f7', col: 'lg:col-span-4', variant: 'dark' },
  { key: 'sec', tag: 'SEC', title: 'Cyber Security', desc: 'Pen-testing, audits, hardening and 24/7 SIEM/SOC operations. Find vulnerabilities first, stop threats fast, and stay compliant.', image: '/legacy/images/new/cyber.png', accent: '#67e8f9', col: 'lg:col-span-3', variant: 'dark' },
  { key: 'dm', tag: 'DM', title: 'Digital Marketing', desc: 'SEO, paid campaigns and content strategy that drive measurable growth. Data-driven optimisation with transparent ROI reporting.', image: '/legacy/images/new/dm.png', accent: '#fbbf24', col: 'lg:col-span-4', variant: 'dark' },
  { key: 'biz', tag: 'BIZ', title: 'Business Consulting', desc: 'Strategy, process optimisation and technology roadmaps for sustainable growth. Better decisions, backed by data and experience.', image: '/legacy/images/team.jpg', accent: '#818cf8', col: 'lg:col-span-5', variant: 'dark' },
  { key: 'smm', tag: 'SMM', title: 'Social Media Management', desc: 'End-to-end social presence: content calendars, community, paid social and analytics. Build engaged audiences across every platform.', image: '/legacy/images/new/smm.png', accent: '#0b1226', col: 'lg:col-span-3', variant: 'accent' },
]

const PROJECT_TABS = ['Websites', 'Apps', 'Social Media', "Video Ad's", 'Trainings']

// Filter tabs for the new "Selected work" projects section. Prepends "All".
const PROJECT_FILTER_TABS = ['All', 'Websites', 'Apps', 'Social Media', 'Trainings']

// How many rows the homepage index shows before deferring to /portfolio.
const HOME_PROJECT_LIMIT = 6

// 6 fictional client projects across categories for the editorial 3x2 grid.
/* The fallback list, used only until the portfolio API answers. Client
   performance figures are deliberately not part of it. */
/* One mapping for showcase rows, used by both the live fetch and the baked
   fallback so the two can never describe the same project differently. */
function mapShowcase(rows) {
  const colors = ['#67e8f9', '#a78bfa', '#38bdf8', '#fb9a8f', '#fbbf24', '#34d399']
  return (rows || []).map((p, idx) => ({
    id: String(p.id),
    name: p.title,
    category: p.categories,
    year: Number(p.year),
    client: 'Spectranox Client',
    tagline: `A custom project designed with ${p.tech_stack}.`,
    description: p.description || '',
    countryCode: p.country_code || '',
    image: showcaseImage(p.project_image),
    tags: String(p.tech_stack || '').split(',').map((t) => t.trim()).filter(Boolean),
    color: colors[idx % colors.length],
  }))
}

/* The invented placeholder roster that used to live here has been removed.
   It seeded the homepage with six fictional clients, and because it was
   only ever replaced on a SUCCESSFUL fetch, a database outage left that
   fiction on the page. The real content is baked into the build instead;
   see src/lib/contentFallback.js. */

const STEPS = [
  { n: 1, title: 'Write or paste script', text: "Quickly draft your message or drop in your ready-to-go text to get started instantly. Whether you're crafting a new idea, refining existing", img: '/legacy/images/ns-img-554.png', extra: '/legacy/images/ns-img-555.svg', extraPos: 'absolute top-[100px] right-[-11px] z-20 object-cover md:top-[149px]' },
  { n: 2, title: 'Choose your voice style', text: 'Select from natural human-like voices, accents, and tones that fit your content. Browse through our extensive library of professional voices, each designed to match', img: '/legacy/images/ns-img-556.png' },
  { n: 3, title: 'Customize the delivery', text: 'Adjust speed, pitch, pauses, and emotions for a perfect performance. Fine-tune every aspect of your voiceover to create the exact tone and pacing you need for your project.', img: '/legacy/images/ns-img-557.png' },
  { n: 4, title: 'Generate & download', text: "Get studio-quality audio in seconds and download instantly. Your professional voiceover is ready to use in any format, whether you're creating content for videos, podcasts, or presentations.", img: '/legacy/images/ns-img-558.png', extra: '/legacy/images/ns-img-559.svg', extraPos: 'absolute top-[149px] left-1/2 z-20 -translate-x-1/2 object-cover' },
]

export const TESTIMONIALS = [
  {
    avatar: '/legacy/images/avatar/avatar-3.png',
    name: 'Srinivas Kalyan',
    country: 'Bengaluru',
    role: 'Co-founder & CTO',
    company: 'Silicon Minds Technologies',
    quote: 'Students used to phone us to ask which track suited them. Now they compare Design Verification, Physical Design, DFT and RTL themselves and enrol through the placement funnel without us in the middle.',
    video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    poster: '/images/remote/unsplash-a4e0d6f367.jpg',
    rating: 5,
    featured: true,
    highlight: '4',
    highlightLabel: 'VLSI tracks live',
    category: 'Training · Semiconductors',
    duration: '02:38',
  },
  {
    avatar: '/legacy/images/avatar/avatar-1.png',
    name: 'Hari Prasad Reddy',
    country: 'Hyderabad',
    role: 'Managing Director',
    company: 'OceanCore Technologies',
    quote: 'Our buyers are engineers. They want the process nodes and the delivery record before they will take a call, and the site puts all of it in front of them. Enquiries now arrive already qualified.',
    video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    poster: '/images/remote/unsplash-d423ef8ec1.jpg',
    rating: 5,
    duration: '02:14',
  },
  {
    avatar: '/legacy/images/avatar/avatar-2.png',
    name: 'Marek Kowalczyk',
    country: 'Warsaw',
    role: 'Co-founder',
    company: 'JobifyPL',
    quote: 'Seekers, recruiters and advertisers all wanted different things, and we were told that meant three builds. We got one, and it holds up on a phone as well as it does on a desktop.',
    video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    poster: '/images/remote/unsplash-c5b6d931ab.jpg',
    rating: 5,
    duration: '01:47',
  },
  {
    avatar: '/legacy/images/avatar/avatar-4.png',
    name: 'Kristaps Ozolins',
    country: 'Riga',
    role: 'Director',
    company: 'S360 Consultancy',
    quote: 'Two languages, three audiences, one content backend. My team writes a listing once and it goes live in English and Latvian together. That alone gave us back a day a week.',
    rating: 5,
  },
  {
    avatar: '/legacy/images/avatar/avatar-5.png',
    name: 'Aravind Balaji',
    country: 'Chennai',
    role: 'Managing Director',
    company: 'Nexora Tech Solutions',
    quote: 'The 3D hero is what people remember, but the taxonomy underneath it is what sells. Visitors find the right service and reach the quote form without anyone walking them through it.',
    rating: 5,
  },
  {
    avatar: '/legacy/images/avatar/avatar-6.png',
    name: 'Divya Sree Kolluri',
    country: 'Vijayawada',
    role: 'Co-founder',
    company: 'Vastara Varaya',
    quote: 'Measurements used to live in a notebook and half of them went missing. Customers book the fitting and their sizes stay on the order, so nothing has to be taken twice.',
    rating: 5,
  },
]

/* Countries we've delivered into. Flags are self-hosted SVGs under
   /images/flags so the marquee never depends on a third-party CDN. */
const COUNTRIES = [
  { name: 'India', flag: '/images/flags/in.svg' },
  { name: 'Poland', flag: '/images/flags/pl.svg' },
  { name: 'Latvia', flag: '/images/flags/lv.svg' },
  { name: 'United Kingdom', flag: '/images/flags/gb.svg' },
  { name: 'USA', flag: '/images/flags/us.svg' },
  { name: 'Canada', flag: '/images/flags/ca.svg' },
  { name: 'Germany', flag: '/images/flags/de.svg' },
  { name: 'Netherlands', flag: '/images/flags/nl.svg' },
  { name: 'Australia', flag: '/images/flags/au.svg' },
  { name: 'UAE', flag: '/images/flags/ae.svg' },
  { name: 'Singapore', flag: '/images/flags/sg.svg' },
]

/* ---------------- small reusable bits ---------------- */

function CheckIcon() {
  return (
    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="fill-white dark:fill-secondary">
      <path d="M4.31661 6.75605L9.74905 1.42144C10.0836 1.0959 10.0836 0.569702 9.74905 0.244158C9.41446 -0.081386 8.87363 -0.081386 8.53904 0.244158L3.7116 4.99012L1.46096 2.78807C1.12636 2.46253 0.585538 2.46253 0.250945 2.78807C-0.0836483 3.11362 -0.0836483 3.63982 0.250945 3.96536L3.1066 6.75605C3.27347 6.91841 3.49253 7 3.7116 7C3.93067 7 4.14974 6.91841 4.31661 6.75605Z" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none" aria-hidden="true">
      <path d="M9.45202 0.5H11.2924L7.27177 5.58308L12.0017 12.5H8.29819L5.3975 8.30492L2.07844 12.5H0.236996L4.53741 7.06308L0 0.5H3.7975L6.41947 4.33446L9.45202 0.5ZM8.80612 11.2815H9.82587L3.24339 1.65446H2.1491L8.80612 11.2815Z" className="fill-secondary dark:fill-accent" />
    </svg>
  )
}

function Counter({ value, suffix = '', duration = 2000 }) {
  const { ref, inView } = useInView()
  const n = useCountUp(value, { start: inView, duration })
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  )
}

/* ─────────────  STAT CARD (Track Record section)  ─────────────
   Four big-number cards used in the stats section. Each card has a
   tinted accent colour, a soft hover-lift, a glowing icon pill, and
   an animated counter. Grid is grid-cols-2 → lg:grid-cols-4 so it
   becomes a 2×2 layout on phones and a single row on desktop. */
function StatIcon({ type, size = 22 }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': true }
  if (type === 'check') {
    return (
      <svg {...common}>
        <path d="M22 11.0799V11.9999C21.9988 14.1563 21.3005 16.2545 20.0093 17.9817C18.7182 19.7088 16.9033 20.9723 14.8354 21.5838C12.7674 22.1952 10.5573 22.1218 8.53447 21.3744C6.51168 20.6271 4.78465 19.246 3.61096 17.4369C2.43727 15.6279 1.87979 13.4879 2.02168 11.3362C2.16356 9.18443 2.99721 7.13619 4.39828 5.49694C5.79935 3.85768 7.69279 2.71525 9.79619 2.24001C11.8996 1.76477 14.1003 1.9822 16.07 2.85986" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 4L12 14.01L9 11.01" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (type === 'users') {
    return (
      <svg {...common}>
        <path d="M23 21V19C22.9993 18.1146 22.7044 17.2536 22.1614 16.5532C21.6184 15.8527 20.8581 15.3524 20 15.1309" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 3.13C16.86 3.35 17.62 3.85 18.17 4.55C18.71 5.25 19.01 6.12 19.01 7.01C19.01 7.89 18.71 8.76 18.17 9.46C17.62 10.16 16.86 10.66 16 10.88" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 11C11.21 11 13 9.21 13 7C13 4.79 11.21 3 9 3C6.79 3 5 4.79 5 7C5 9.21 6.79 11 9 11Z" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (type === 'like') {
    return (
      <svg {...common}>
        <path d="M7 22H4C3.47 22 2.96 21.79 2.59 21.41C2.21 21.04 2 20.53 2 20V13C2 12.47 2.21 11.96 2.59 11.59C2.96 11.21 3.47 11 4 11H7M14 9V5C14 4.20 13.68 3.44 13.12 2.88C12.56 2.32 11.80 2 11 2L7 11V22H18.28C18.76 22 19.23 21.84 19.6 21.52C19.97 21.21 20.21 20.78 20.28 20.30L21.66 11.30C21.70 11.01 21.68 10.72 21.60 10.44C21.52 10.16 21.38 9.91 21.19 9.69C21.00 9.47 20.77 9.29 20.50 9.18C20.24 9.06 19.95 8.99 19.66 9H14Z" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (type === 'globe') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9.5" stroke="#1A1A1C" strokeWidth="2.2" />
        <ellipse cx="12" cy="12" rx="4" ry="9.5" stroke="#1A1A1C" strokeWidth="2.2" />
        <path d="M2.5 12h19" stroke="#1A1A1C" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    )
  }
  return null
}

/**
 * Service masonry card. Uses a dark glass card with a real image at top, a
 * subtle accent-colored hover glow, and two CTAs (Request Quote + See Details).
 * Pass `service.variant: 'accent'` to render the lime-green highlighted card.
 */
function ServiceCard({ service, index, className = '' }) {
  const isAccent = service.variant === 'accent'
  const numStr = String(index ?? 0).padStart(2, '0')
  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl transition-all duration-500 hover:-translate-y-1.5 ${isAccent
          ? 'bg-gradient-to-br from-[#c2ff5c] via-[#38bdf8] to-[#a3e635] shadow-lg shadow-lime-500/20 hover:shadow-xl hover:shadow-lime-500/50'
          : 'border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-sm hover:border-white/30'
        } ${className}`}
    >
      {!isAccent && (
        <>
          {/* Soft accent glow that fades in on hover */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{ background: `radial-gradient(70% 60% at 50% 0%, ${service.accent}30, transparent 70%)` }}
          />
          {/* Animated accent line that sweeps across the top edge on hover */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px origin-left scale-x-0 transition-transform duration-700 group-hover:scale-x-100"
            style={{ background: `linear-gradient(to right, transparent, ${service.accent}, transparent)` }}
          />
        </>
      )}

      {/* Image — aspect varies per card so masonry can stagger naturally */}
      <div className="relative overflow-hidden" style={{ aspectRatio: service.aspect ?? '16 / 9' }}>
        <img
          src={service.img}
          alt={service.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
        {!isAccent && (
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-950/75 via-ink-950/10 to-transparent" />
        )}
        {/* Numbered badge top-left */}
        <span
          className={`absolute left-2.5 top-2.5 inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[9px] font-bold tracking-wider backdrop-blur-md ${isAccent ? 'bg-ink-950/85 text-lime-300' : 'bg-ink-950/65 text-white/90 ring-1 ring-white/20'
            }`}
        >
          {numStr}
        </span>
        {/* Service tag top-right with accent-coloured pulsing dot */}
        <span
          className={`absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.18em] backdrop-blur-md ${isAccent ? 'bg-ink-950/85 text-lime-300' : 'bg-ink-950/65 text-white ring-1 ring-white/20'
            }`}
        >
          <span
            aria-hidden
            className="size-1 rounded-full animate-pulse"
            style={{ background: isAccent ? '#38bdf8' : service.accent }}
          />
          {service.tag}
        </span>
      </div>

      {/* Compact content */}
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <h3 className={`text-base font-bold leading-tight tracking-tight sm:text-lg lg:text-xl ${isAccent ? 'text-ink-950' : 'text-white'}`}>
          {service.title}
        </h3>
        <p className={`mt-1.5 line-clamp-3 text-xs leading-relaxed sm:text-[13px] ${isAccent ? 'text-ink-950/80' : 'text-slate-400'}`}>
          {service.desc}
        </p>

        {/* Compact CTAs */}
        <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-2.5">
          <Link
            to="/contact"
            className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all hover:-translate-y-0.5 sm:px-4 sm:py-2 ${isAccent
                ? 'bg-ink-950 text-white hover:bg-ink-900'
                : 'bg-white text-ink-950 hover:bg-slate-100'
              }`}
          >
            Request quote
            <svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
          <Link
            to="/services"
            className={`group/link inline-flex items-center gap-0.5 text-xs font-semibold transition-colors ${isAccent ? 'text-ink-950 hover:text-ink-700' : 'text-slate-300 hover:text-white'
              }`}
          >
            See details
            <svg className="size-3 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 17 17 7M7 7h10v10" />
            </svg>
          </Link>
        </div>
      </div>
    </article>
  )
}

/**
 * Compact horizontal service card. Image occupies ~42% of card width on the
 * left, content fills the rest. Heights are pinned (~175px on desktop) so the
 * whole 6-card masonry fits in one viewport. Accent variant renders the lime
 * green Social Media card with dark text and dark CTAs.
 */
function SpxServiceCard({ svc, colSpan }) {
  const isAccent = svc.variant === 'accent'

  return (
    <article
      className={[
        'group relative flex flex-col overflow-hidden rounded-2xl transition-all duration-500 sm:flex-row',
        'min-h-[200px] sm:h-[200px] lg:h-[210px]',
        'hover:-translate-y-1 hover:shadow-[0_25px_60px_-20px_rgba(56,189,248,0.35)]',
        isAccent
          ? 'text-white ring-1 ring-white/15'
          : 'bg-white/[0.04] text-white ring-1 ring-white/10 hover:ring-white/20 backdrop-blur-sm',
        colSpan,
      ].join(' ')}
      style={
        isAccent
          ? { background: 'linear-gradient(135deg, #0b1226 0%, #1b2a55 40%, #38bdf8 75%, #a855f7 110%)' }
          : undefined
      }
    >
      {/* soft accent glow that reveals on hover (dark cards) */}
      {!isAccent && (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: `radial-gradient(60% 80% at 50% 0%, ${svc.accent}25, transparent 70%)` }}
        />
      )}

      {/* IMAGE (~44% on the left) */}
      <div className="relative h-36 w-full shrink-0 overflow-hidden sm:h-full sm:w-[44%]">
        <img
          src={svc.image}
          alt={svc.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.08]"
        />
        {/* accent tint over the image */}
        <div
          aria-hidden
          className="absolute inset-0 mix-blend-overlay opacity-30 transition-opacity duration-500 group-hover:opacity-50"
          style={{ background: `linear-gradient(135deg, ${isAccent ? '#a855f7' : svc.accent}66, transparent 65%)` }}
        />
        {/* dark fade into the content panel */}
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/30 sm:to-black/10" />

        {/* tag chip */}
        <span
          className="absolute left-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-black/45 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur"
        >
          <span aria-hidden className="size-1 rounded-full" style={{ background: isAccent ? '#7dd3fc' : svc.accent }} />
          {svc.tag}
        </span>
      </div>

      {/* CONTENT */}
      <div className="relative flex min-w-0 flex-1 flex-col justify-between p-4 sm:p-5">
        <div>
          <h3 className="font-display text-[17px] font-bold leading-tight tracking-tight text-white sm:text-[18px]">
            {svc.title}
          </h3>
          <p className={`mt-2 line-clamp-3 text-[12.5px] leading-[1.5] ${isAccent ? 'text-white/85' : 'text-white/60'}`}>
            {svc.desc}
          </p>
        </div>

        {/* single bold CTA */}
        <Link
          to="/contact"
          className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[11.5px] font-semibold transition-all duration-300 hover:-translate-y-0.5"
          style={{
            color: isAccent ? '#0b1226' : svc.accent,
            borderColor: isAccent ? '#7dd3fc' : `${svc.accent}55`,
            background: isAccent ? '#7dd3fc' : `${svc.accent}18`,
          }}
        >
          View details
          <span aria-hidden className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">→</span>
        </Link>
      </div>
    </article>
  )
}

/** Small accent-coloured tag chip used in the bento service cards. */
function SvcTag({ color, label, position, dark = false }) {
  return (
    <span
      className={`absolute z-10 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] backdrop-blur ${position} ${dark
          ? 'border-[#0a1505]/30 bg-[#c2ff5c]/40 text-[#0a1505]'
          : 'border-white/15 bg-[#0a0f1e]/70'
        }`}
      style={!dark ? { color } : undefined}
    >
      <span aria-hidden className="size-1 rounded-full" style={{ background: dark ? '#0a1505' : color }} />
      {label}
    </span>
  )
}

/** Single primary CTA (Request quote pill) shared by every bento card.
 *  `compact` shrinks padding for the small bottom-row tiles. */
function SvcCTAs({ compact = false, to = '/services', primaryBg, primaryText }) {
  const pillPad = compact ? 'px-3 py-1.5 text-[11px]' : 'px-3.5 py-2 text-[12px]'
  return (
    <div className="mt-auto flex items-center pt-2.5">
      <Link
        to={to}
        className={`inline-flex items-center gap-1 rounded-full font-semibold tracking-tight transition-all hover:gap-1.5 ${pillPad} ${primaryBg} ${primaryText}`}
      >
        View details
        <span aria-hidden>→</span>
      </Link>
    </div>
  )
}

/**
 * ProjectFilters — editorial cinema redesign.
 * Filter pills + a featured-project canvas on the left and a clickable
 * numbered scroll list on the right. Click a row to swap the feature.
 */
function ProjectFilters({ active = 'All', setActive = () => { }, projects = [] }) {
  const [featuredId, setFeaturedId] = useState(projects[0]?.id || '')
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false)

  useEffect(() => {
    if (projects.length > 0 && !projects.find((p) => p.id === featuredId)) {
      setFeaturedId(projects[0].id)
    }
  }, [projects, featuredId])

  const filtered = active === 'All'
    ? projects
    : projects.filter((p) => {
        if (!p.category) return false
        const cats = p.category.split(',').map((c) => c.trim().toLowerCase())
        return cats.includes(active.toLowerCase())
      })

  // Reset feature when filter changes so the spotlight always exists in the list
  useEffect(() => {
    const shown = filtered.slice(0, HOME_PROJECT_LIMIT)
    if (!shown.find((p) => p.id === featuredId) && shown.length > 0) {
      setFeaturedId(shown[0].id)
    }
  }, [active, filtered, featuredId])

  // The homepage teases a handful; the rest live on /portfolio.
  const visible = filtered.slice(0, HOME_PROJECT_LIMIT)

  const featured = visible.find((p) => p.id === featuredId) || visible[0]
  const featuredIdx = visible.findIndex((p) => p.id === featured?.id)

  if (!featured) {
    return (
      <div className="mx-auto w-full max-w-[1240px] py-16 text-center text-sm text-slate-500">
        No projects in this category yet, ask us about upcoming work.
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1240px]">

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        {/* ===== LEFT — featured spotlight ===== */}
        <div className="w-full lg:col-span-7">
          <div
            key={featured.id}
            className="w-full relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.2)]"
            style={{ animation: 'svc-reveal 0.6s cubic-bezier(0.22,1,0.36,1) both' }}
          >
            {/* image with diagonal accent stripe */}
            <div className="relative overflow-hidden bg-slate-100">
              <img
                src={featured.image}
                alt={featured.name}
                className="block w-full h-auto transition-transform duration-700 ease-out hover:scale-[1.04]"
              />
            </div>

            {/* unified footer strip — Title + Stack */}
            <div className="flex flex-col gap-3 px-5 py-4 sm:px-7 lg:px-9">
              {/* The spotlight sits on white, so the tag takes the light palette. */}
              <CountryTag code={featured.countryCode} tone="light" className="self-start" />
              <h3 className="font-display text-lg sm:text-xl font-bold tracking-tight text-slate-900">
                {featured.name}
              </h3>
              {/* Clamped to 3 lines so every project keeps the card the same
                  height, however long the copy the admin saved. */}
              <p
                className="text-sm leading-relaxed text-slate-600"
                style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {featured.description || featured.tagline}
              </p>
            </div>
          </div>
        </div>

        {/* ===== RIGHT — clickable index list ===== */}
        <div className="w-full lg:col-span-5">
          <ul className="w-full divide-y divide-slate-100 overflow-hidden rounded-3xl border border-slate-200/70 bg-white">
            {visible.map((p, i) => {
              const isActive = p.id === featured.id
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setFeaturedId(p.id)
                      if (window.innerWidth < 1024) {
                        setIsMobileModalOpen(true)
                      }
                    }}
                    className={`group/row relative flex w-full items-center gap-4 px-5 py-4 text-left transition-colors ${isActive ? 'bg-slate-50' : 'hover:bg-slate-50/60'
                      }`}
                  >
                    {/* active accent rail */}
                    <span
                      aria-hidden
                      className={`absolute left-0 top-0 h-full w-[3px] transition-all ${isActive ? 'opacity-100' : 'opacity-0'}`}
                      style={{ backgroundColor: p.color }}
                    />

                    {/* numbered cell — hidden on phones, where the row needs
                        every pixel for the title and the category line */}
                    <span
                      className={`hidden font-sans text-[12px] font-medium tracking-normal transition-colors sm:inline ${isActive ? 'text-slate-900' : 'text-slate-400 group-hover/row:text-slate-600'
                        }`}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>

                    {/* thumb — 16:9, matching the shape of the site screenshots
                        it holds, so they crop instead of squashing */}
                    <span
                      className={`relative aspect-video w-20 shrink-0 overflow-hidden rounded-lg border transition-all ${isActive ? 'border-slate-300 ring-2 ring-offset-2' : 'border-slate-200 group-hover/row:border-slate-300'
                        }`}
                      style={isActive ? { '--tw-ring-color': p.color } : undefined}
                    >
                      <img src={p.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" />
                    </span>

                    {/* meta */}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-slate-900">
                        {p.name}
                      </span>
                      <span className="mt-0.5 block truncate font-sans text-[12px] tracking-normal text-slate-500">
                        {p.category} · {p.year}
                      </span>
                    </span>

                    {/* arrow */}
                    <span
                      aria-hidden
                      className={`grid size-7 place-items-center rounded-full border transition-all ${isActive
                          ? 'border-transparent text-slate-900'
                          : 'border-slate-200 text-slate-400 group-hover/row:border-slate-300 group-hover/row:text-slate-700'
                        }`}
                      style={isActive ? { backgroundColor: p.color } : undefined}
                    >
                      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>

          {/* "Click any row to spotlight" hint stays with the list it explains */}
          <p className="mt-4 px-1 text-center font-sans text-[12px] tracking-normal text-slate-400 sm:text-left">
            Click any row to spotlight
          </p>
        </div>
      </div>

      {/* Centered link to the full archive — sits below BOTH columns so it reads
          as an action for the whole section, not just the index list */}
      <div className="mt-10 flex justify-center">
        <Link
          to="/portfolio"
          className="group/all inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 font-sans text-[13px] font-semibold text-slate-800 transition-all hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-md"
        >
          See our work
          <svg viewBox="0 0 24 24" className="h-4 w-4 transition-transform duration-300 group-hover/all:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
      </div>

      {/* Mobile Spotlight Modal */}
      {isMobileModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
          {/* Modal Card */}
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-white/10 bg-[#0e1329] shadow-2xl">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setIsMobileModalOpen(false)}
              className="absolute right-4 top-4 z-10 grid size-9 place-items-center rounded-full shadow-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#ffffff' }}
              aria-label="Close modal"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Content */}
            <div className="w-full overflow-hidden bg-slate-900">
              <img
                src={featured.image}
                alt={featured.name}
                className="block w-full h-auto"
              />
            </div>

            <div className="p-6">
              {/* Which market the work was built for, read before the name —
                  the same tag the portfolio and case study pages carry. */}
              <CountryTag code={featured.countryCode} />

              <div className="mt-3 font-display text-2xl font-bold leading-tight text-neutral-100" style={{ color: '#ffffff' }}>
                {featured.name}
              </div>

              <p
                className="mt-4 text-sm leading-relaxed"
                style={{
                  color: 'rgba(255, 255, 255, 0.72)',
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}
              >
                {featured.description || featured.tagline}
              </p>

              {/* Closing the modal on the way out, so returning with the back
                  button does not land on a stale overlay. */}
              <Link
                to={`/portfolio/${featured.id}`}
                onClick={() => setIsMobileModalOpen(false)}
                className="btn-on-solid mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)', boxShadow: '0 16px 34px -14px rgba(37,99,235,0.75)' }}
              >
                View project details
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** Single project card — image strip on top with category/year overlay,
 *  then title, tagline, tag row + case-study CTA. */
function ProjectCard({ project }) {
  const { name, tagline, image, color } = project

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-ink-950 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-white/20 hover:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)]">
      {/* image */}
      <div className="relative h-[180px] overflow-hidden bg-[#0f1530]">
        <img
          src={image}
          alt={name}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
        />
      </div>

      {/* content */}
      <div className="flex flex-1 flex-col px-5 pb-5 pt-5">
        <h3 className="font-display text-[20px] leading-tight tracking-tight text-white transition-colors duration-300 group-hover:text-brand-300">
          {name}
        </h3>
        <p className="mt-2 line-clamp-3 text-[13.5px] leading-relaxed text-slate-300">{tagline}</p>


      </div>
    </div>
  )
}

function ShieldIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
      <path d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

function SkullIcon({ className = '', style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C7 2 3 6 3 11c0 2.5 1.1 4.8 2.8 6.4l.2.2V20c0 .6.4 1 1 1h1v-2h2v2h4v-2h2v2h1c.6 0 1-.4 1-1v-2.4l.2-.2C19.9 15.8 21 13.5 21 11c0-5-4-9-9-9zM8.5 12c-.8 0-1.5-.7-1.5-1.5S7.7 9 8.5 9s1.5.7 1.5 1.5S9.3 12 8.5 12zm7 0c-.8 0-1.5-.7-1.5-1.5S14.7 9 15.5 9s1.5.7 1.5 1.5S16.3 12 15.5 12zm-3 4l-1-2-1 2h2z" />
    </svg>
  )
}

function LockIcon({ className = '', style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

function BrainIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 0 7 4.5v.59A3 3 0 0 0 4 8v.5a3 3 0 0 0-1.5 5.5 3 3 0 0 0 1 2.32A2.5 2.5 0 0 0 7 19.5v.5a2.5 2.5 0 0 0 5 0V4.5A2.5 2.5 0 0 0 9.5 2Z" />
      <path d="M14.5 2A2.5 2.5 0 0 1 17 4.5v.59A3 3 0 0 1 20 8v.5a3 3 0 0 1 1.5 5.5 3 3 0 0 1-1 2.32A2.5 2.5 0 0 1 17 19.5v.5a2.5 2.5 0 0 1-5 0V4.5A2.5 2.5 0 0 1 14.5 2Z" />
    </svg>
  )
}

function Spinner({ className = '' }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

/**
 * The hero's bottom card — replaces the static aipowered.png with a live
 * animated panel: pulsing AI brain, terminal that types code line-by-line,
 * cycling task indicator and a shimmering progress bar. Matches the original
 * cyan/blue gradient and aspect ratio so the cluster layout is unchanged.
 */
function AIAutomationCard() {
  const [typed, setTyped] = useState('')
  const [lineIdx, setLineIdx] = useState(0)
  const [taskIdx, setTaskIdx] = useState(0)

  // Typewriter effect — types current line, then advances after a pause.
  useEffect(() => {
    const target = CODE_LINES[lineIdx]
    if (typed.length < target.length) {
      const t = setTimeout(() => setTyped(target.slice(0, typed.length + 1)), 38)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => {
      setTyped('')
      setLineIdx((i) => (i + 1) % CODE_LINES.length)
    }, 1300)
    return () => clearTimeout(t)
  }, [typed, lineIdx])

  // Cycle through "currently running" tasks.
  useEffect(() => {
    const id = setInterval(() => setTaskIdx((i) => (i + 1) % AI_TASKS.length), 2100)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="relative block w-full overflow-hidden" style={{ aspectRatio: '820 / 280' }}>
      {/* ===================================================================
          LAYER 1 — animated workspace (sits underneath, revealed by the wipe).
          =================================================================== */}

      {/* Cyan/blue gradient base (matches the original aipowered.png). No
          blurred decorative orbs — the gradient is enough and avoids costly
          continuous filter:blur() animations that hang low-end devices. */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-[#BFEBFB] via-[#88CEEC] to-[#5DB1D5]" />

      {/* Content */}
      <div className="relative grid h-full grid-cols-[1.05fr_1fr] gap-3 p-4 sm:p-5 lg:p-6">
        {/* LEFT — branding */}
        <div className="flex min-w-0 flex-col justify-center">
          <h3 className="font-bold leading-[1.1] text-slate-900 text-base sm:text-lg lg:text-xl xl:text-2xl">
            AI Powered<br />Software Solutions
          </h3>
        </div>

        {/* RIGHT — animated AI workspace */}
        <div className="flex min-w-0 flex-col justify-center gap-1.5 sm:gap-2">
          {/* AI brain indicator */}
          <div className="flex items-center gap-2">
            <div className="relative grid size-7 place-items-center rounded-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-500 shadow-lg shadow-violet-600/40">
              <BrainIcon className="size-3.5 text-white" />
              <span aria-hidden className="absolute inset-0 rounded-full ring-2 ring-cyan-200/80 animate-ping" />
            </div>
            <span className="min-w-0 truncate font-mono text-[10px] font-semibold text-slate-900 sm:text-[11px]">{typeof window !== 'undefined' ? window.location.hostname : site.name.toLowerCase()}</span>
            <span aria-hidden className="ml-auto size-1.5 rounded-full bg-emerald-500 animate-pulse" />
          </div>

          {/* Typing terminal */}
          <div className="h-[20px] overflow-hidden whitespace-nowrap rounded-md bg-slate-900/95 px-2.5 py-1 font-mono text-[9px] text-[#34d399] shadow-inner sm:h-[24px] sm:text-[10px]">
            <span className="text-[#34d399]/70">$</span> {typed}
            <span aria-hidden className="ml-0.5 inline-block h-2.5 w-1 -translate-y-[1px] bg-[#34d399] align-middle animate-pulse" />
          </div>

          {/* Active task */}
          <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1 shadow-sm backdrop-blur sm:px-2.5 sm:py-1.5">
            <Spinner className="size-3 shrink-0 text-violet-600" />
            <span key={taskIdx} className="truncate text-[10px] font-medium text-slate-700 sm:text-[11px]">
              {AI_TASKS[taskIdx]}
            </span>
            <span className="ml-auto font-mono text-[9px] text-slate-500 sm:text-[10px]">
              {String(taskIdx + 1).padStart(2, '0')}/{String(AI_TASKS.length).padStart(2, '0')}
            </span>
          </div>

          {/* Shimmer progress */}
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/40">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-cyan-500 animate-pulse-glow"
              style={{ width: `${((taskIdx + 1) / AI_TASKS.length) * 100}%`, transition: 'width 700ms ease' }}
            />
          </div>
        </div>
      </div>

      {/* ===================================================================
          LAYER 2 — original image, gets clipped from left → right by the
          scanner, completely gone once the wipe finishes.
          =================================================================== */}
      <img
        src="/legacy/images/new/aipowered.png"
        alt="AI Powered Software Solutions"
        aria-hidden="true"
        /* This is the page's LCP element — tell the browser so up front.
           Lowercase: React 18 passes unknown lowercase attributes straight
           through, but warns on the camelCase spelling it doesn't know. */
        fetchpriority="high"
        decoding="async"
        className="pointer-events-none absolute inset-0 z-40 h-full w-full object-cover animate-ai-wipe"
      />

      {/* ===================================================================
          LAYER 3 — glossy white scanner line that travels left → right,
          painting away the image as it goes. Fades in at start, out at end.
          =================================================================== */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 bottom-0 z-50 w-[3px] animate-ai-scan"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.55) 15%, #ffffff 50%, rgba(255,255,255,0.55) 85%, transparent 100%)',
          boxShadow:
            '0 0 12px 2px rgba(255,255,255,1), 0 0 28px 6px rgba(167,243,208,0.85), 0 0 60px 14px rgba(74,222,128,0.45)',
        }}
      />
    </div>
  )
}

/* Mock threat-log entries shown in the scrolling SOC feed. Severity 0=critical
   (red), 1=high (amber), 2=medium (yellow). Action codes mirror SIEM tools. */
const CYBER_LOGS = [
  { ip: '203.0.113.42', vec: 'SQLi', act: 'BLOCK', sev: 0 },
  { ip: '198.51.100.7', vec: 'XSS', act: 'DROP', sev: 1 },
  { ip: '172.16.041.91', vec: 'DDoS', act: 'QUAR', sev: 0 },
  { ip: '10.0.255.032', vec: 'RCE', act: 'BLOCK', sev: 0 },
  { ip: '203.0.113.91', vec: 'TR0J', act: 'QUAR', sev: 0 },
  { ip: '198.51.100.8', vec: 'CSRF', act: 'DROP', sev: 1 },
  { ip: '192.168.1.142', vec: 'BFRC', act: 'BLOCK', sev: 1 },
  { ip: '203.0.113.55', vec: 'PHSH', act: 'DROP', sev: 2 },
]

const RETICLES = [
  { left: '22%', top: '28%', delay: 0.3, label: 'MAL.K3X' },
  { left: '56%', top: '40%', delay: 0.55, label: 'INTRUSION' },
  { left: '78%', top: '30%', delay: 0.80, label: '0xDEADBEEF' },
  { left: '42%', top: '68%', delay: 1.05, label: 'CVE-2024-31337' },
]

/**
 * Hero's cyber card — replaces the static cyber.png with a professional
 * SOC-style live dashboard: hex-armored shield with sonar pings, a scrolling
 * SIEM threat log (IP + attack vector + action), severity-coded indicators
 * and a live "blocked" counter. The original image dissolves via a
 * targeting-reticle lock-on + RGB-shifted glitch — no scanner wipe.
 */


function StepCard({ step }) {
  return (
    <div id={`step-${step.n}`} data-step={step.n} className="step-card bg-ns-ivory relative mx-auto w-[320px] shrink-0 overflow-hidden rounded-4xl p-6 sm:w-[350px] md:h-[476px] md:w-[400px] xl:w-[442px]">
      <figure className="bg-ns-linen relative z-10 rounded-[20px] p-2">
        <img src={step.img} alt={`step ${step.n}`} className="size-full object-cover" />
        {step.extra && <img src={step.extra} alt={`step ${step.n}`} className={step.extraPos} />}
      </figure>
      <div className="pointer-events-none absolute bottom-[77px] left-1/2 z-30 h-[262px] w-[641px] -translate-x-1/2 rotate-180 md:bottom-[52px] xl:bottom-[22px]" style={{ background: 'linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, #f4efe7 37.44%)' }} />
      <div className="relative z-33 mt-16 space-y-3 md:mt-14 lg:mt-6">
        <h2 className="text-heading-5 lg:text-heading-4 font-normal">{step.title}</h2>
        <p className="text-tagline-2">{step.text}</p>
      </div>
    </div>
  )
}

function PhaseIcon({ name, className = 'h-5 w-5' }) {
  const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' }
  switch (name) {
    case 'compass':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
          <circle cx="12" cy="12" r="9" {...s} />
          <path d="m15.5 8.5-2 5-5 2 2-5z" {...s} />
        </svg>
      )
    case 'code':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
          <path d="m8 8-4 4 4 4M16 8l4 4-4 4M14 4l-4 16" {...s} />
        </svg>
      )
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
          <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9.5 4.5-1 8-4.5 8-9.5V6Z" {...s} />
          <path d="m9 12 2 2 4-4" {...s} />
        </svg>
      )
    case 'rocket':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
          <path d="M14 4c4 0 6 2 6 6 0 5-6 11-9 12-1-3-1-7 1-10s5-8 2-8Z" {...s} />
          <path d="m9 15-3 3-1-4 4-1" {...s} />
          <circle cx="14" cy="10" r="1.4" {...s} />
        </svg>
      )
    case 'orbit':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
          <ellipse cx="12" cy="12" rx="9" ry="3.6" {...s} />
          <ellipse cx="12" cy="12" rx="9" ry="3.6" transform="rotate(60 12 12)" {...s} />
          <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
        </svg>
      )
    default:
      return null
  }
}

/* ============================================================
   PhaseDiagram — editorial micro-diagram per phase. Sits in the
   header of each PhaseCard. Each phase has a distinct visual:
   - compass: a sweep arc + bearings
   - code:    bracket frame + line counts
   - shield:  shield outline + status reads
   - rocket:  trajectory arc + dust trail
   - orbit:   nested ellipses + a satellite that orbits
   ============================================================ */
function PhaseDiagram({ name, accent, idx }) {
  const stroke = `${accent}cc`
  const common = { fill: 'none', stroke, strokeWidth: 1.2, strokeLinecap: 'round', strokeLinejoin: 'round' }

  if (name === 'compass') {
    return (
      <svg viewBox="0 0 72 36" className="h-9 w-[72px]" aria-hidden="true">
        <circle cx="14" cy="18" r="11" stroke="rgba(255,255,255,0.12)" strokeWidth="1" fill="none" />
        <circle cx="14" cy="18" r="11" pathLength="1" strokeDasharray="0.3 0.7" strokeDashoffset="0.7" {...common} />
        <path d="M14 9 L17 16 L21 18 L14 18 Z" fill={accent} stroke="none" opacity="0.9" />
        <circle cx="14" cy="18" r="1.2" fill={accent} stroke="none" />
        <line x1="30" y1="12" x2="68" y2="12" stroke="rgba(255,255,255,0.08)" strokeWidth="0.7" strokeDasharray="2 3" />
        <line x1="30" y1="24" x2="68" y2="24" stroke="rgba(255,255,255,0.08)" strokeWidth="0.7" strokeDasharray="2 3" />
        <text x="32" y="14" fontSize="5.5" fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace" fill="rgba(255,255,255,0.45)" letterSpacing="0.08em">N 28°</text>
        <text x="32" y="26" fontSize="5.5" fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace" fill="rgba(255,255,255,0.45)" letterSpacing="0.08em">E 77°</text>
      </svg>
    )
  }

  if (name === 'code') {
    return (
      <svg viewBox="0 0 72 36" className="h-9 w-[72px]" aria-hidden="true">
        <text x="4" y="22" fontSize="12" fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace" fill={stroke} letterSpacing="-0.05em">{'<'}</text>
        <text x="60" y="22" fontSize="12" fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace" fill={stroke} letterSpacing="-0.05em">{'/>'}</text>
        {[
          { w: 24, y: 8 },
          { w: 32, y: 16 },
          { w: 20, y: 24 },
        ].map((l, i) => (
          <g key={i}>
            <rect x="16" y={l.y} width={l.w} height="2.5" rx="1" fill="rgba(255,255,255,0.12)" />
            <rect x="16" y={l.y} width="3" height="2.5" rx="1" fill={accent} />
          </g>
        ))}
      </svg>
    )
  }

  if (name === 'shield') {
    return (
      <svg viewBox="0 0 72 36" className="h-9 w-[72px]" aria-hidden="true">
        <path d="M14 4 L23 7 V20 C23 25 19 28 14 30 C9 28 5 25 5 20 V7 Z" {...common} />
        <path d="M10 17 L13 20 L19 13" {...common} />
        {[
          { y: 10, label: 'E2E' },
          { y: 18, label: 'A11Y' },
          { y: 26, label: 'SEC' },
        ].map((row, i) => (
          <g key={i}>
            <circle cx="30" cy={row.y} r="1.6" fill={accent} />
            <line x1="34" y1={row.y} x2="68" y2={row.y} stroke="rgba(255,255,255,0.1)" strokeWidth="0.7" />
            <text x="36" y={row.y + 1.8} fontSize="5.2" fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace" fill="rgba(255,255,255,0.55)" letterSpacing="0.1em">{row.label}</text>
          </g>
        ))}
      </svg>
    )
  }

  if (name === 'rocket') {
    return (
      <svg viewBox="0 0 72 36" className="h-9 w-[72px]" aria-hidden="true">
        <path d="M4 30 Q24 6 66 4" {...common} strokeDasharray="2 2" />
        <circle cx="4" cy="30" r="1.6" fill="rgba(255,255,255,0.55)" />
        {[0, 1, 2, 3].map(i => (
          <circle key={i} cx={60 - i * 5} cy={6 + i * 2.5} r={0.9 - i * 0.18} fill={accent} opacity={0.7 - i * 0.15} />
        ))}
        <g transform="translate(66 4) rotate(50)">
          <path d="M0 -3.5 L4.5 0 L0 3.5 L-2.2 1 L-4.5 3.5 L-3.4 0 L-4.5 -3.5 L-2.2 -1 Z" fill={accent} stroke="none" />
        </g>
      </svg>
    )
  }

  // orbit
  const orbitPathId = `wf-orbit-${idx}`
  return (
    <svg viewBox="0 0 72 36" className="h-9 w-[72px]" aria-hidden="true">
      <defs>
        <ellipse id={orbitPathId} cx="36" cy="18" rx="22" ry="9" fill="none" stroke="none" />
      </defs>
      <ellipse cx="36" cy="18" rx="30" ry="6" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8" fill="none" strokeDasharray="2 3" />
      <ellipse cx="36" cy="18" rx="22" ry="9" stroke={`${accent}a8`} strokeWidth="1.1" fill="none" />
      <circle cx="36" cy="18" r="2.6" fill={accent} stroke="none" />
      <circle r="1.3" fill={accent}>
        <animateMotion dur="6.5s" repeatCount="indefinite">
          <mpath href={`#${orbitPathId}`} />
        </animateMotion>
      </circle>
    </svg>
  )
}

/* ============================================================
   LaunchServicesCard — sequential service-category storyteller.
   Replaces launch.png with six fully-rendered, animated scenes
   that auto-cycle to convey each Spectranox service category one
   at a time: Apps → Websites → Drones → IoT → Growth → Revenue.
   The original image dissolves via a CRT power-off effect first,
   then the rotation runs continuously beneath.
   ============================================================ */

const COLOR_DOT = {
  violet: 'bg-violet-400',
  cyan: 'bg-cyan-400',
  sky: 'bg-sky-400',
  amber: 'bg-amber-400',
  rose: 'bg-rose-400',
  emerald: 'bg-emerald-400',
}


function PhoneMockup({ children, className = '', style }) {
  return (
    <div
      className={`relative h-[110px] w-[58px] rounded-[8px] bg-slate-900 ring-[1.5px] ring-slate-700/80 shadow-xl shadow-black/60 ${className}`}
      style={style}
    >
      {/* notch */}
      <span aria-hidden className="absolute left-1/2 top-1 h-1 w-3 -translate-x-1/2 rounded-full bg-slate-700/70" />
      {/* screen */}
      <div className="absolute inset-1 top-[10px] overflow-hidden rounded-[5px] bg-slate-950 p-1">{children}</div>
      {/* glass highlight */}
      <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[8px] bg-gradient-to-br from-white/[0.06] to-transparent" />
    </div>
  )
}

/* ─────────────  Scene 1: APP UI DESIGN  ───────────── */
function AppsScene() {
  const Phone = ({ rotation, delay, zIndex, translateY, children }) => (
    <div
      className="animate-float relative"
      style={{
        transform: `rotate(${rotation}deg) translateY(${translateY}px)`,
        animationDelay: `${delay}s`,
        zIndex,
      }}
    >
      <div className="relative h-[112px] w-[56px] rounded-[10px] bg-slate-900 ring-[1.5px] ring-slate-700 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.6)]">
        <div className="absolute inset-[2px] rounded-[8px] bg-black overflow-hidden">
          <div className="absolute inset-[1.5px] rounded-[7px] bg-[#0b0d18] overflow-hidden">
            {/* Status bar */}
            <div className="flex items-center justify-between px-1.5 pt-[3px] pb-[2px]">
              <span className="text-white text-[5px] font-mono font-bold leading-none">9:41</span>
              <div className="flex items-center gap-[2px]">
                <svg width="5" height="4" viewBox="0 0 5 4"><rect x="0" y="2.5" width="0.8" height="1.5" fill="white" /><rect x="1.2" y="1.8" width="0.8" height="2.2" fill="white" /><rect x="2.4" y="1" width="0.8" height="3" fill="white" /><rect x="3.6" y="0.2" width="0.8" height="3.8" fill="white" /></svg>
                <svg width="5" height="4" viewBox="0 0 5 4"><path d="M2.5 1 Q0.5 1 0 2 L2.5 4 L5 2 Q4.5 1 2.5 1Z" fill="white" /></svg>
                <div className="w-[5px] h-[2.5px] rounded-[0.5px] border border-white/70 relative">
                  <div className="absolute inset-[0.3px] bg-emerald-400 rounded-[0.3px]" style={{ width: '70%' }} />
                </div>
              </div>
            </div>
            {/* Dynamic island */}
            <div className="absolute top-[2px] left-1/2 -translate-x-1/2 h-[5px] w-[16px] rounded-full bg-black" />
            {children}
            {/* Glass overlay */}
            <div className="absolute inset-0 pointer-events-none rounded-[7px]" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 40%, transparent 100%)' }} />
          </div>
        </div>
        {/* Side buttons */}
        <div className="absolute -left-[1px] top-[18px] h-[6px] w-[0.5px] bg-slate-700 rounded-full" />
        <div className="absolute -left-[1px] top-[28px] h-[10px] w-[0.5px] bg-slate-700 rounded-full" />
        <div className="absolute -right-[1px] top-[24px] h-[14px] w-[0.5px] bg-slate-700 rounded-full" />
      </div>
    </div>
  );

  const FitnessApp = () => (
    <div className="px-1.5 pt-[1px]">
      <div className="flex items-center justify-between mb-[3px]">
        <span className="text-white text-[6px] font-bold tracking-tight">Today</span>
        <div className="h-[6px] w-[6px] rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500" />
      </div>
      <div className="flex justify-center mb-[3px]">
        <svg width="34" height="34" viewBox="0 0 34 34">
          <circle cx="17" cy="17" r="15" fill="none" stroke="#1e1b3a" strokeWidth="2.5" />
          <circle cx="17" cy="17" r="15" fill="none" stroke="#a78bfa" strokeWidth="2.5" strokeDasharray="75 94" strokeLinecap="round" transform="rotate(-90 17 17)" />
          <circle cx="17" cy="17" r="11.5" fill="none" stroke="#2a1b3a" strokeWidth="2.5" />
          <circle cx="17" cy="17" r="11.5" fill="none" stroke="#e879f9" strokeWidth="2.5" strokeDasharray="55 72" strokeLinecap="round" transform="rotate(-90 17 17)" />
          <circle cx="17" cy="17" r="8" fill="none" stroke="#1a2e2a" strokeWidth="2.5" />
          <circle cx="17" cy="17" r="8" fill="none" stroke="#34d399" strokeWidth="2.5" strokeDasharray="42 50" strokeLinecap="round" transform="rotate(-90 17 17)" />
        </svg>
      </div>
      <div className="space-y-[2px] mb-[3px]">
        <div className="flex justify-between text-[5px] font-mono"><span className="text-violet-300">Steps</span><span className="text-white tabular-nums">8,432</span></div>
        <div className="flex justify-between text-[5px] font-mono"><span className="text-fuchsia-400">Cal</span><span className="text-white tabular-nums">1,247</span></div>
        <div className="flex justify-between text-[5px] font-mono"><span className="text-emerald-400">Dist</span><span className="text-white tabular-nums">5.2km</span></div>
      </div>
      <div className="h-[10px] mb-[3px] bg-black/40 rounded-[2px] px-[2px] flex items-center">
        <svg width="44" height="8" viewBox="0 0 44 8">
          <polyline points="0,4 4,4 6,4 8,1 10,7 12,2 14,4 18,4 22,4 24,1 26,7 28,2 30,4 34,4 38,4 40,1 42,7 44,4" fill="none" stroke="#f472b6" strokeWidth="0.6" strokeLinejoin="round" className="animate-pulse" />
        </svg>
      </div>
      <div className="absolute bottom-[3px] left-0 right-0 flex justify-around">
        <div className="h-[3px] w-[3px] rounded-full bg-violet-400" />
        <div className="h-[3px] w-[3px] rounded-full bg-slate-600" />
        <div className="h-[3px] w-[3px] rounded-full bg-slate-600" />
        <div className="h-[3px] w-[3px] rounded-full bg-slate-600" />
      </div>
    </div>
  );

  const BankingApp = () => (
    <div className="px-1.5 pt-[1px]">
      <div className="rounded-[3px] p-[3px] mb-[3px] relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #c026d3 60%, #ec4899 100%)' }}>
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.4), transparent 50%)' }} />
        <div className="relative">
          <div className="text-[4px] text-white/70 font-mono uppercase tracking-wide">Balance</div>
          <div className="text-white text-[8px] font-bold tabular-nums leading-tight">₹10,67,450</div>
          <div className="text-[4px] text-white/80 font-mono mt-[1px]">•••• 4829</div>
        </div>
      </div>
      <div className="flex gap-[2px] mb-[3px]">
        <div className="flex-1 text-[4.5px] text-violet-200 bg-violet-500/20 rounded-[2px] py-[1.5px] text-center font-medium">Send</div>
        <div className="flex-1 text-[4.5px] text-violet-200 bg-violet-500/20 rounded-[2px] py-[1.5px] text-center font-medium">Request</div>
        <div className="flex-1 text-[4.5px] text-violet-200 bg-violet-500/20 rounded-[2px] py-[1.5px] text-center font-medium">Pay</div>
      </div>
      <div className="space-y-[2px] mb-[3px]">
        {[
          { dot: 'bg-violet-400', name: 'Linear', amt: '-₹2,000', neg: true },
          { dot: 'bg-fuchsia-500', name: 'Vercel', amt: '-₹1,650', neg: true },
          { dot: 'bg-emerald-400', name: 'Payout', amt: '+₹1,03,000', neg: false },
        ].map((t, i) => (
          <div key={i} className="flex items-center gap-[2px]">
            <div className={`h-[3px] w-[3px] rounded-full ${t.dot}`} />
            <span className="text-[5px] text-white font-medium flex-1 truncate">{t.name}</span>
            <span className={`text-[5px] font-mono tabular-nums ${t.neg ? 'text-white/70' : 'text-emerald-400'}`}>{t.amt}</span>
          </div>
        ))}
      </div>
      <div className="flex h-[4px] rounded-full overflow-hidden bg-black/40">
        <div className="bg-violet-400" style={{ width: '34%' }} />
        <div className="bg-fuchsia-500" style={{ width: '26%' }} />
        <div className="bg-pink-400" style={{ width: '18%' }} />
        <div className="bg-emerald-400" style={{ width: '14%' }} />
      </div>
    </div>
  );

  const FoodApp = () => (
    <div className="px-1.5 pt-[1px]">
      <div className="flex items-center justify-between mb-[3px]">
        <span className="text-white text-[6px] font-bold tracking-tight">Nearby</span>
        <svg width="6" height="6" viewBox="0 0 6 6"><path d="M3 0.5 C1.5 0.5 0.5 1.5 0.5 3 C0.5 4.5 3 5.5 3 5.5 C3 5.5 5.5 4.5 5.5 3 C5.5 1.5 4.5 0.5 3 0.5 Z M3 3.5 A0.7 0.7 0 1 1 3 2.1 A0.7 0.7 0 0 1 3 3.5Z" fill="#a78bfa" /></svg>
      </div>
      {[
        { name: 'Noru Ramen', rating: '4.8', time: '20 min', price: '$$', illus: 'ramen' },
        { name: 'Soba & Co', rating: '4.6', time: '25 min', price: '$$', illus: 'burger' },
      ].map((r, i) => (
        <div key={i} className="flex gap-[3px] items-center mb-[2.5px] bg-white/[0.03] rounded-[2px] p-[2px]">
          <div className="h-[12px] w-[12px] rounded-[2px] flex-shrink-0 flex items-center justify-center" style={{ background: r.illus === 'ramen' ? 'linear-gradient(135deg,#fb7185,#c026d3)' : 'linear-gradient(135deg,#a78bfa,#ec4899)' }}>
            {r.illus === 'ramen' ? (
              <svg width="9" height="9" viewBox="0 0 9 9">
                <ellipse cx="4.5" cy="6" rx="3.5" ry="1.5" fill="#fef3c7" />
                <path d="M1 5.5 Q4.5 4 8 5.5 L8 6 Q4.5 4.5 1 6 Z" fill="#fde047" />
                <circle cx="3" cy="5.3" r="0.5" fill="#86efac" />
                <circle cx="5.5" cy="5.5" r="0.4" fill="#fca5a5" />
                <path d="M2 4 Q2.5 3 3 4" stroke="white" strokeWidth="0.3" fill="none" opacity="0.6" />
              </svg>
            ) : (
              <svg width="9" height="9" viewBox="0 0 9 9">
                <ellipse cx="4.5" cy="3" rx="3" ry="1.2" fill="#fde047" />
                <rect x="1.5" y="3.5" width="6" height="1" fill="#86efac" />
                <rect x="1.3" y="4.3" width="6.4" height="1.3" fill="#dc2626" />
                <ellipse cx="4.5" cy="6.2" rx="3" ry="1.2" fill="#d97706" />
                <circle cx="3" cy="3" r="0.3" fill="white" opacity="0.6" />
                <circle cx="5.5" cy="3.2" r="0.25" fill="white" opacity="0.6" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[5px] text-white font-semibold leading-tight truncate">{r.name}</div>
            <div className="text-[4px] text-violet-200/70 font-mono leading-tight">
              <span className="text-amber-300">{r.rating}★</span> · {r.time}
            </div>
            <div className="text-[4px] text-emerald-400 font-mono leading-tight">{r.price}</div>
          </div>
        </div>
      ))}
      <div className="absolute bottom-[3px] left-1.5 right-1.5 rounded-full py-[2px] text-center text-[5px] font-bold text-white" style={{ background: 'linear-gradient(90deg, #a78bfa, #e879f9)' }}>
        Order · 22 min
      </div>
    </div>
  );

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0f1e]">
      {/* Radial violet glow */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(167,139,250,0.18), transparent 70%)' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 40% 60% at 50% 100%, rgba(232,121,249,0.15), transparent 70%)' }} />

      {/* Floating interaction pings */}
      <div className="absolute top-[20%] left-[18%] h-1 w-1 rounded-full bg-violet-400 animate-ping" />
      <div className="absolute top-[70%] left-[28%] h-[3px] w-[3px] rounded-full bg-fuchsia-400 animate-ping" style={{ animationDelay: '0.7s' }} />
      <div className="absolute top-[15%] right-[22%] h-1 w-1 rounded-full bg-pink-400 animate-ping" style={{ animationDelay: '1.2s' }} />
      <div className="absolute top-[75%] right-[18%] h-[3px] w-[3px] rounded-full bg-violet-300 animate-ping" style={{ animationDelay: '0.3s' }} />
      <div className="absolute top-[40%] left-[8%] h-[2px] w-[2px] rounded-full bg-violet-400/60" />
      <div className="absolute top-[55%] right-[8%] h-[2px] w-[2px] rounded-full bg-fuchsia-400/60" />

      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(167,139,250,1) 1px, transparent 1px), linear-gradient(90deg, rgba(167,139,250,1) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

      {/* Phones */}
      <div className="absolute inset-0 flex items-center justify-center gap-[6px]">
        <Phone rotation={-12} delay={0} zIndex={1} translateY={6}>
          <FitnessApp />
        </Phone>
        <Phone rotation={0} delay={0.5} zIndex={3} translateY={-4}>
          <BankingApp />
        </Phone>
        <Phone rotation={12} delay={1} zIndex={1} translateY={6}>
          <FoodApp />
        </Phone>
      </div>

      {/* Top corner label */}
      <div className="absolute top-2 left-2 flex items-center gap-1">
        <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[7px] font-mono text-violet-200/80 uppercase tracking-[0.15em]">apps.live</span>
      </div>
      <div className="absolute top-2 right-2 text-[7px] font-mono text-violet-200/60 tabular-nums">3 / 24</div>
    </div>
  );
}

/* ─────────────  Scene 2: WEB DEVELOPMENT  ───────────── */
function WebsScene() {
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0f1e]" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {/* Radial vignette */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(34,211,238,0.10), transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(167,139,250,0.06), transparent 55%)' }} />

      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(125,211,252,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(125,211,252,0.4) 1px, transparent 1px)', backgroundSize: '14px 14px' }} />

      {/* Secondary browser window (behind, tilted) */}
      <div className="absolute" style={{ top: '14px', left: '14px', width: '230px', height: '150px', transform: 'rotate(-6deg)', opacity: 0.55 }}>
        <div className="h-full w-full rounded-md ring-1 ring-slate-700 bg-slate-900 shadow-2xl overflow-hidden">
          {/* chrome */}
          <div className="h-[14px] bg-slate-800 flex items-center px-1.5 gap-1 border-b border-slate-700/60">
            <div className="flex gap-[2px]">
              <div className="h-[5px] w-[5px] rounded-full bg-red-400/80" />
              <div className="h-[5px] w-[5px] rounded-full bg-amber-400/80" />
              <div className="h-[5px] w-[5px] rounded-full bg-emerald-400/80" />
            </div>
            <div className="ml-1.5 flex gap-[2px]">
              <div className="h-[8px] w-[28px] rounded-t-sm bg-slate-700/70" />
              <div className="h-[8px] w-[34px] rounded-t-sm bg-slate-900 flex items-center px-1">
                <span className="text-[3px] text-slate-300 font-medium tracking-tight">helio.co</span>
              </div>
            </div>
            <div className="ml-auto h-[6px] w-[40px] rounded-sm bg-slate-700/60" />
          </div>
          {/* page */}
          <div className="p-2 h-[calc(100%-14px)] bg-gradient-to-br from-slate-900 via-slate-900 to-violet-950/40">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[5px] font-bold text-slate-200 tracking-tight">helio</div>
              <div className="flex gap-1">
                <div className="text-[3.5px] text-slate-400">Product</div>
                <div className="text-[3.5px] text-slate-400">Pricing</div>
                <div className="text-[3.5px] text-slate-400">Docs</div>
              </div>
            </div>
            <div className="text-[3px] text-violet-300 font-mono tracking-widest mb-1">NEW · v2.4 LAUNCH</div>
            <div className="text-[10px] font-bold text-slate-100 leading-[1.05] tracking-tight mb-1">
              Build faster.<br />
              <span className="text-violet-300">Ship sharper.</span>
            </div>
            <div className="text-[4px] text-slate-400 leading-tight mb-2 max-w-[80%]">
              The all-in-one workspace for modern product teams. Plan, build, and launch in one tool.
            </div>
            <div className="flex gap-1 mb-2">
              <div className="px-1.5 py-[2px] rounded-sm bg-violet-400 text-[4px] font-semibold text-slate-900">Get started</div>
              <div className="px-1.5 py-[2px] rounded-sm ring-1 ring-slate-600 text-[4px] text-slate-300">Watch demo</div>
            </div>
            {/* product mockup placeholder */}
            <div className="h-[42px] rounded-sm bg-slate-950/70 ring-1 ring-slate-700/60 p-1 flex gap-1">
              <div className="w-[28px] rounded-sm bg-slate-800/70 flex flex-col gap-[2px] p-[2px]">
                <div className="h-[2px] w-full rounded-sm bg-violet-400/60" />
                <div className="h-[1.5px] w-3/4 rounded-sm bg-slate-700" />
                <div className="h-[1.5px] w-full rounded-sm bg-slate-700" />
                <div className="h-[1.5px] w-2/3 rounded-sm bg-slate-700" />
                <div className="h-[1.5px] w-1/2 rounded-sm bg-slate-700" />
              </div>
              <div className="flex-1 rounded-sm bg-slate-800/40 p-[2px]">
                <div className="flex items-end gap-[2px] h-full">
                  <div className="w-[3px] h-[30%] bg-violet-400/50 rounded-sm" />
                  <div className="w-[3px] h-[55%] bg-violet-400/60 rounded-sm" />
                  <div className="w-[3px] h-[40%] bg-violet-400/70 rounded-sm" />
                  <div className="w-[3px] h-[75%] bg-cyan-400/80 rounded-sm" />
                  <div className="w-[3px] h-[60%] bg-violet-400/60 rounded-sm" />
                  <div className="w-[3px] h-[90%] bg-cyan-400/90 rounded-sm" />
                  <div className="w-[3px] h-[50%] bg-violet-400/60 rounded-sm" />
                  <div className="w-[3px] h-[70%] bg-cyan-400/80 rounded-sm" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN browser window (foreground, floating) */}
      <div className="absolute animate-float" style={{ top: '10px', left: '70px', width: '298px', height: '172px' }}>
        <div className="h-full w-full rounded-md ring-1 ring-slate-700 bg-slate-900 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.6)] overflow-hidden relative">
          {/* scan-line texture overlay */}
          <div className="absolute inset-0 pointer-events-none z-20" style={{ background: 'repeating-linear-gradient(0deg, rgba(125,211,252,0.04) 0px, rgba(125,211,252,0.04) 1px, transparent 1px, transparent 3px)' }} />

          {/* CHROME BAR */}
          <div className="h-[16px] bg-slate-800 flex items-center px-2 gap-1.5 border-b border-slate-700/60 relative z-10">
            {/* traffic lights */}
            <div className="flex gap-1">
              <div className="h-[6px] w-[6px] rounded-full bg-red-400" />
              <div className="h-[6px] w-[6px] rounded-full bg-amber-400" />
              <div className="h-[6px] w-[6px] rounded-full bg-emerald-400" />
            </div>
            {/* tabs */}
            <div className="ml-2 flex items-end gap-[2px] -mb-[1px]">
              <div className="h-[11px] w-[42px] rounded-t-[3px] bg-slate-700/50 flex items-center px-1">
                <div className="h-[4px] w-[4px] rounded-sm bg-slate-500 mr-1" />
                <span className="text-[4px] text-slate-400 truncate">draft.fig</span>
              </div>
              <div className="h-[11px] w-[40px] rounded-t-[3px] bg-slate-700/50 flex items-center px-1">
                <div className="h-[4px] w-[4px] rounded-sm bg-slate-500 mr-1" />
                <span className="text-[4px] text-slate-400 truncate">notion</span>
              </div>
              <div className="h-[12px] w-[68px] rounded-t-[3px] bg-slate-900 flex items-center px-1.5 ring-1 ring-slate-700/60 ring-b-0">
                <div className="h-[4px] w-[4px] rounded-full bg-cyan-400 mr-1" />
                <span className="text-[4.5px] text-slate-200 font-medium truncate flex-1">Spectranox, Work</span>
                <svg width="5" height="5" viewBox="0 0 8 8" className="text-slate-500"><path d="M1 1 L7 7 M7 1 L1 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
              </div>
            </div>
          </div>

          {/* NAV ROW (back/forward/refresh + address bar) */}
          <div className="h-[14px] bg-slate-800/70 flex items-center px-2 gap-1.5 border-b border-slate-700/40 relative z-10">
            <svg width="7" height="7" viewBox="0 0 10 10" className="text-slate-400"><path d="M6.5 2 L3 5 L6.5 8" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <svg width="7" height="7" viewBox="0 0 10 10" className="text-slate-500"><path d="M3.5 2 L7 5 L3.5 8" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <svg width="7" height="7" viewBox="0 0 10 10" className="text-slate-400"><path d="M2 5 A3 3 0 1 1 5 8" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" /><path d="M2 3 L2 5 L4 5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {/* address bar pill */}
            <div className="flex-1 h-[10px] rounded-full bg-slate-900/80 ring-1 ring-slate-700/60 flex items-center px-1.5 gap-1">
              <svg width="6" height="6" viewBox="0 0 8 8" className="text-emerald-400"><path d="M2 4 L2 2.5 A2 2 0 0 1 6 2.5 L6 4" stroke="currentColor" strokeWidth="0.9" fill="none" /><rect x="1.5" y="4" width="5" height="3.5" rx="0.5" fill="currentColor" /></svg>
              <span className="text-[4.5px] text-slate-300 tracking-tight font-mono">spectranox.studio/work</span>
              <div className="ml-auto flex items-center gap-1">
                <svg width="6" height="6" viewBox="0 0 8 8" className="text-slate-400"><circle cx="2" cy="4" r="1" fill="currentColor" /><circle cx="6" cy="2" r="1" fill="currentColor" /><circle cx="6" cy="6" r="1" fill="currentColor" /><path d="M2.7 3.7 L5.3 2.3 M2.7 4.3 L5.3 5.7" stroke="currentColor" strokeWidth="0.5" /></svg>
              </div>
            </div>
            <div className="flex gap-[2px]">
              <div className="h-[5px] w-[5px] rounded-full bg-slate-600" />
              <div className="h-[5px] w-[5px] rounded-full bg-slate-600" />
              <div className="h-[5px] w-[5px] rounded-full bg-slate-600" />
            </div>
          </div>

          {/* BOOKMARK BAR */}
          <div className="h-[7px] bg-slate-800/40 flex items-center px-2 gap-1.5 border-b border-slate-700/30 relative z-10">
            <div className="h-[3px] w-[3px] rounded-full bg-cyan-400" />
            <div className="h-[3px] w-[3px] rounded-full bg-violet-400" />
            <div className="h-[3px] w-[3px] rounded-full bg-sky-400" />
            <div className="h-[3px] w-[3px] rounded-full bg-emerald-400" />
          </div>

          {/* PAGE CONTENT */}
          <div className="px-3 py-2 h-[calc(100%-37px)] bg-slate-900 relative overflow-hidden">
            {/* page top nav */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <svg width="7" height="7" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4" fill="none" stroke="#22d3ee" strokeWidth="1" /><circle cx="5" cy="5" r="1.5" fill="#22d3ee" /></svg>
                <span className="text-[6px] font-bold text-slate-100 tracking-tight">spectranox</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[4.5px] text-slate-300">Work</span>
                <span className="text-[4.5px] text-slate-400">Studio</span>
                <span className="text-[4.5px] text-slate-400">Process</span>
                <span className="text-[4.5px] text-slate-400">Journal</span>
                <span className="text-[4.5px] text-slate-400">Contact</span>
                <div className="px-1.5 py-[2px] rounded-sm bg-slate-800 ring-1 ring-slate-700 text-[4.5px] text-cyan-300 font-medium">Let's talk</div>
              </div>
            </div>

            {/* HERO */}
            <div className="mb-2">
              <div className="text-[3.5px] font-mono text-cyan-400/80 tracking-[0.2em] mb-[2px]">PORTFOLIO · 2025</div>
              <div className="text-[15px] font-bold text-slate-100 leading-[0.95] tracking-tight mb-[2px]">
                Design that <span className="text-cyan-300">ships.</span>
              </div>
              <div className="text-[4px] text-slate-400 leading-tight mb-1.5 max-w-[70%]">
                A creative studio crafting digital products, brand systems, and interfaces for ambitious teams.
              </div>
              <div className="flex items-center gap-1.5">
                <div className="relative">
                  <div className="px-2 py-[3px] rounded-sm bg-cyan-300 text-[4.5px] font-semibold text-slate-900 tracking-tight">Start a project</div>
                  <div className="absolute inset-0 rounded-sm ring-2 ring-cyan-300/60 animate-ping" />
                </div>
                <div className="px-2 py-[3px] rounded-sm ring-1 ring-slate-700 text-[4.5px] text-slate-300">View work →</div>
              </div>
            </div>

            {/* PROJECT GRID */}
            <div className="grid grid-cols-3 gap-1.5 mb-1">
              {/* card 1 — gradient blob */}
              <div className="rounded-sm ring-1 ring-slate-700/60 bg-slate-800/30 overflow-hidden">
                <div className="h-[24px] relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0c4a6e, #155e75)' }}>
                  <svg viewBox="0 0 80 30" className="absolute inset-0 w-full h-full">
                    <defs>
                      <radialGradient id="blob1" cx="0.3" cy="0.4">
                        <stop offset="0%" stopColor="#67e8f9" />
                        <stop offset="100%" stopColor="#0e7490" stopOpacity="0" />
                      </radialGradient>
                    </defs>
                    <ellipse cx="24" cy="14" rx="22" ry="14" fill="url(#blob1)" />
                    <ellipse cx="58" cy="18" rx="14" ry="10" fill="#a78bfa" opacity="0.5" />
                  </svg>
                </div>
                <div className="p-1">
                  <div className="text-[4.5px] font-semibold text-slate-100 tracking-tight">Aurora Banking</div>
                  <div className="text-[3.5px] text-slate-400 font-mono">Branding · Web</div>
                </div>
              </div>
              {/* card 2 — geometric pattern */}
              <div className="rounded-sm ring-1 ring-slate-700/60 bg-slate-800/30 overflow-hidden">
                <div className="h-[24px] relative overflow-hidden bg-slate-950">
                  <svg viewBox="0 0 80 30" className="absolute inset-0 w-full h-full">
                    <g stroke="#22d3ee" strokeWidth="0.5" fill="none" opacity="0.6">
                      <circle cx="20" cy="15" r="10" />
                      <circle cx="20" cy="15" r="6" />
                      <circle cx="20" cy="15" r="2" />
                    </g>
                    <g stroke="#a78bfa" strokeWidth="0.5" fill="none" opacity="0.8">
                      <rect x="40" y="6" width="18" height="18" />
                      <rect x="44" y="10" width="10" height="10" />
                    </g>
                    <line x1="58" y1="6" x2="76" y2="24" stroke="#67e8f9" strokeWidth="0.5" />
                    <line x1="76" y1="6" x2="58" y2="24" stroke="#67e8f9" strokeWidth="0.5" />
                  </svg>
                </div>
                <div className="p-1">
                  <div className="text-[4.5px] font-semibold text-slate-100 tracking-tight">Helix Health</div>
                  <div className="text-[3.5px] text-slate-400 font-mono">Product · UX</div>
                </div>
              </div>
              {/* card 3 — abstract typography */}
              <div className="rounded-sm ring-1 ring-slate-700/60 bg-slate-800/30 overflow-hidden">
                <div className="h-[24px] relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e1b4b, #0f172a)' }}>
                  <svg viewBox="0 0 80 30" className="absolute inset-0 w-full h-full">
                    <text x="6" y="22" fontSize="20" fontWeight="900" fill="#22d3ee" opacity="0.9" fontFamily="sans-serif" letterSpacing="-1">Nb</text>
                    <text x="30" y="22" fontSize="20" fontWeight="900" fill="none" stroke="#a78bfa" strokeWidth="0.4" fontFamily="sans-serif" letterSpacing="-1">o7</text>
                    <text x="54" y="22" fontSize="20" fontWeight="900" fill="#67e8f9" opacity="0.5" fontFamily="sans-serif" letterSpacing="-1">∞</text>
                  </svg>
                </div>
                <div className="p-1">
                  <div className="text-[4.5px] font-semibold text-slate-100 tracking-tight">Nimbus IoT</div>
                  <div className="text-[3.5px] text-slate-400 font-mono">Brand · Identity</div>
                </div>
              </div>
            </div>

            {/* footer row */}
            <div className="text-[3.5px] text-slate-500 font-mono tracking-wider">150+ PROJECTS · 12 AWARDS · EST. 2017</div>
          </div>
        </div>
      </div>

      {/* Custom cursor over "Start a project" button */}
      <div className="absolute z-30 pointer-events-none" style={{ top: '108px', left: '155px' }}>
        <svg width="12" height="14" viewBox="0 0 12 14" className="drop-shadow-lg">
          <path d="M1 1 L1 11 L4 8.5 L6 12.5 L7.5 11.8 L5.5 7.8 L9 7.8 Z" fill="#f8fafc" stroke="#0f172a" strokeWidth="0.6" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Lighthouse-style performance chip */}
      <div className="absolute z-30 bottom-2 right-2 flex items-center gap-1 px-1.5 py-1 rounded-md bg-slate-900/90 ring-1 ring-slate-700 backdrop-blur-sm">
        <div className="relative h-[16px] w-[16px]">
          <svg viewBox="0 0 20 20" className="absolute inset-0">
            <circle cx="10" cy="10" r="8" fill="none" stroke="#1e293b" strokeWidth="2" />
            <circle cx="10" cy="10" r="8" fill="none" stroke="#34d399" strokeWidth="2" strokeDasharray="50.2" strokeDashoffset="1" strokeLinecap="round" transform="rotate(-90 10 10)" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-[5.5px] font-bold text-emerald-300">98</div>
        </div>
        <div className="flex flex-col leading-none">
          <span className="text-[4px] font-mono text-slate-400 tracking-[0.15em]">PERFORMANCE</span>
          <span className="text-[3.5px] font-mono text-emerald-400/80 tracking-wider">LIGHTHOUSE</span>
        </div>
      </div>

      {/* Top-left status badge */}
      <div className="absolute z-30 top-2 left-2 flex items-center gap-1 px-1.5 py-[2px] rounded-full bg-slate-900/80 ring-1 ring-cyan-400/30 backdrop-blur-sm">
        <div className="h-[4px] w-[4px] rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-[4px] font-mono text-cyan-300 tracking-[0.15em]">LIVE PREVIEW</span>
      </div>
    </div>
  )
}

/* ─────────────  Scene 3: DRONE  ───────────── */
function DroneScene() {
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0f1e] font-mono">
      {/* Background gradient + vignette */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1e] via-[#0b1424] to-[#071018]" />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 55%, rgba(56,189,248,0.10), transparent 60%)' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)' }} />

      {/* Topographic contour lines */}
      <svg className="absolute inset-0 h-full w-full opacity-[0.09]" viewBox="0 0 377 190" preserveAspectRatio="none">
        <g fill="none" stroke="#7dd3fc" strokeWidth="0.4">
          <path d="M0 30 C 60 20, 120 45, 180 35 S 320 25, 377 40" />
          <path d="M0 55 C 70 45, 130 70, 200 60 S 320 50, 377 65" />
          <path d="M0 80 C 60 70, 140 95, 220 85 S 320 75, 377 90" />
          <path d="M0 105 C 80 95, 140 120, 230 110 S 330 100, 377 115" />
          <path d="M0 130 C 60 120, 150 145, 240 135 S 330 125, 377 140" />
          <path d="M0 155 C 70 145, 160 170, 250 160 S 340 150, 377 165" />
          <path d="M0 180 C 80 170, 170 195, 260 185 S 340 175, 377 185" />
        </g>
        <g fill="none" stroke="#22d3ee" strokeWidth="0.25" opacity="0.6">
          <path d="M0 42 C 70 32, 130 57, 210 47 S 320 37, 377 52" />
          <path d="M0 92 C 70 82, 150 107, 230 97 S 330 87, 377 102" />
          <path d="M0 142 C 70 132, 160 157, 250 147 S 330 137, 377 152" />
        </g>
      </svg>

      {/* Grid dots */}
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #7dd3fc 0.5px, transparent 0.5px)', backgroundSize: '14px 14px' }} />

      {/* Radar sweep (centered, masked to circle) */}
      <div className="absolute left-1/2 top-1/2 h-[260px] w-[260px] -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden opacity-40">
        <div className="absolute inset-0 rounded-full border border-sky-400/15" />
        <div className="absolute inset-[20px] rounded-full border border-sky-400/10" />
        <div className="absolute inset-[50px] rounded-full border border-sky-400/10" />
        <div className="absolute inset-[80px] rounded-full border border-sky-400/10" />
        <div className="absolute inset-0 animate-spin-slow" style={{ background: 'conic-gradient(from 0deg, transparent 0deg, transparent 320deg, rgba(56,189,248,0.35) 355deg, rgba(125,211,252,0.55) 360deg)' }} />
      </div>

      {/* Center cross axes (faint) */}
      <div className="absolute left-1/2 top-0 h-full w-px bg-sky-300/5" />
      <div className="absolute top-1/2 left-0 h-px w-full bg-sky-300/5" />

      {/* Targeting reticle at center (over drone area) */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <svg width="90" height="90" viewBox="0 0 90 90">
          <circle cx="45" cy="45" r="28" fill="none" stroke="#7dd3fc" strokeWidth="0.5" strokeDasharray="2 3" opacity="0.55" />
          <circle cx="45" cy="45" r="18" fill="none" stroke="#22d3ee" strokeWidth="0.4" opacity="0.4" />
          {/* Corner brackets of reticle */}
          <path d="M22 30 L22 22 L30 22 M68 22 L68 22 M60 22 L68 22 L68 30 M22 60 L22 68 L30 68 M68 60 L68 68 L60 68" fill="none" stroke="#7dd3fc" strokeWidth="0.7" opacity="0.7" />
          {/* Crosshair */}
          <line x1="45" y1="36" x2="45" y2="42" stroke="#7dd3fc" strokeWidth="0.6" />
          <line x1="45" y1="48" x2="45" y2="54" stroke="#7dd3fc" strokeWidth="0.6" />
          <line x1="36" y1="45" x2="42" y2="45" stroke="#7dd3fc" strokeWidth="0.6" />
          <line x1="48" y1="45" x2="54" y2="45" stroke="#7dd3fc" strokeWidth="0.6" />
          <circle cx="45" cy="45" r="0.8" fill="#7dd3fc" />
        </svg>
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-3 text-[5.5px] tracking-[0.2em] text-sky-300/80 whitespace-nowrap">TRACKING</div>
      </div>

      {/* DRONE - centered, animate-float */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-float">
        {/* Ground shadow */}
        <div className="absolute left-1/2 -translate-x-1/2 top-[88px] h-[10px] w-[110px] rounded-[50%] bg-slate-950/70 blur-md animate-pulse" />

        <svg width="180" height="120" viewBox="0 0 120 80" className="relative">
          <defs>
            <linearGradient id="fuselage" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1e293b" />
              <stop offset="50%" stopColor="#334155" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="arm" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#0f172a" />
              <stop offset="50%" stopColor="#475569" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <radialGradient id="domeGrad" cx="0.35" cy="0.35">
              <stop offset="0%" stopColor="#7dd3fc" />
              <stop offset="40%" stopColor="#0ea5e9" />
              <stop offset="100%" stopColor="#0c4a6e" />
            </radialGradient>
            <radialGradient id="rotorBlur" cx="0.5" cy="0.5">
              <stop offset="0%" stopColor="rgba(125,211,252,0.18)" />
              <stop offset="50%" stopColor="rgba(125,211,252,0.10)" />
              <stop offset="100%" stopColor="rgba(125,211,252,0)" />
            </radialGradient>
            <radialGradient id="rotorRim" cx="0.5" cy="0.5">
              <stop offset="70%" stopColor="rgba(34,211,238,0)" />
              <stop offset="90%" stopColor="rgba(34,211,238,0.4)" />
              <stop offset="100%" stopColor="rgba(34,211,238,0)" />
            </radialGradient>
            <radialGradient id="lensGrad" cx="0.4" cy="0.4">
              <stop offset="0%" stopColor="#475569" />
              <stop offset="60%" stopColor="#0f172a" />
              <stop offset="100%" stopColor="#000" />
            </radialGradient>
            <pattern id="carbon" patternUnits="userSpaceOnUse" width="2" height="2">
              <rect width="2" height="2" fill="#1e293b" />
              <path d="M0 0 L2 2 M2 0 L0 2" stroke="#0f172a" strokeWidth="0.3" />
            </pattern>
          </defs>

          {/* Arms - tapered diagonals */}
          {/* Front-left arm */}
          <polygon points="55,36 22,18 19,21 53,40" fill="url(#arm)" stroke="#0f172a" strokeWidth="0.3" />
          {/* Front-right arm */}
          <polygon points="65,36 98,18 101,21 67,40" fill="url(#arm)" stroke="#0f172a" strokeWidth="0.3" />
          {/* Rear-left arm */}
          <polygon points="55,44 22,62 19,59 53,40" fill="url(#arm)" stroke="#0f172a" strokeWidth="0.3" />
          {/* Rear-right arm */}
          <polygon points="65,44 98,62 101,59 67,40" fill="url(#arm)" stroke="#0f172a" strokeWidth="0.3" />

          {/* Carbon weave overlay on arms (subtle) */}
          <polygon points="55,36 22,18 19,21 53,40" fill="url(#carbon)" opacity="0.35" />
          <polygon points="65,36 98,18 101,21 67,40" fill="url(#carbon)" opacity="0.35" />
          <polygon points="55,44 22,62 19,59 53,40" fill="url(#carbon)" opacity="0.35" />
          <polygon points="65,44 98,62 101,59 67,40" fill="url(#carbon)" opacity="0.35" />

          {/* Camera gimbal below body */}
          <g>
            <rect x="56" y="46" width="8" height="3" rx="1" fill="#0f172a" stroke="#1e293b" strokeWidth="0.3" />
            <ellipse cx="60" cy="51" rx="4" ry="2.5" fill="#0a0f1e" stroke="#1e293b" strokeWidth="0.3" />
            <ellipse cx="60" cy="51" rx="2.5" ry="1.8" fill="url(#lensGrad)" />
            <ellipse cx="60" cy="51" rx="1.5" ry="1" fill="#000" />
            <ellipse cx="59.4" cy="50.6" rx="0.4" ry="0.3" fill="#7dd3fc" opacity="0.9" />
          </g>

          {/* Central body / fuselage */}
          <g>
            {/* Outer shell */}
            <rect x="48" y="32" width="24" height="16" rx="3" fill="url(#fuselage)" stroke="#0f172a" strokeWidth="0.4" />
            {/* Carbon weave */}
            <rect x="48" y="32" width="24" height="16" rx="3" fill="url(#carbon)" opacity="0.4" />
            {/* Top panel highlight */}
            <rect x="50" y="33.5" width="20" height="6" rx="1.5" fill="#1e293b" stroke="#334155" strokeWidth="0.3" />
            <rect x="50.5" y="34" width="19" height="2.5" rx="0.5" fill="#0f172a" />
            {/* Side vents */}
            <line x1="50" y1="44" x2="54" y2="44" stroke="#0a0f1e" strokeWidth="0.4" />
            <line x1="50" y1="45.5" x2="54" y2="45.5" stroke="#0a0f1e" strokeWidth="0.4" />
            <line x1="66" y1="44" x2="70" y2="44" stroke="#0a0f1e" strokeWidth="0.4" />
            <line x1="66" y1="45.5" x2="70" y2="45.5" stroke="#0a0f1e" strokeWidth="0.4" />
            {/* Screws */}
            <circle cx="50" cy="34" r="0.5" fill="#475569" />
            <circle cx="70" cy="34" r="0.5" fill="#475569" />
            <circle cx="50" cy="46" r="0.5" fill="#475569" />
            <circle cx="70" cy="46" r="0.5" fill="#475569" />
          </g>

          {/* Top sensor dome */}
          <g>
            <ellipse cx="60" cy="40" rx="5" ry="3.5" fill="url(#domeGrad)" stroke="#0c4a6e" strokeWidth="0.3" />
            <ellipse cx="58.5" cy="38.8" rx="1.5" ry="0.8" fill="#bae6fd" opacity="0.7" />
            {/* Strobe LED (very brief blinks) */}
            <circle cx="60" cy="40" r="0.6" fill="#fff" />
            <circle cx="60" cy="40" r="1.2" fill="#fff" opacity="0.6" className="animate-ping" />
          </g>

          {/* ROTORS - 4 of them */}
          {/* Front-left rotor */}
          <g>
            <circle cx="20" cy="19.5" r="11" fill="url(#rotorRim)" />
            <circle cx="20" cy="19.5" r="9.5" fill="url(#rotorBlur)" />
            <ellipse cx="20" cy="19.5" rx="9" ry="9" fill="none" stroke="rgba(125,211,252,0.25)" strokeWidth="0.3" />
            {/* Spinning blades group */}
            <g className="animate-spin" style={{ transformOrigin: '20px 19.5px', animationDuration: '0.25s' }}>
              <ellipse cx="20" cy="19.5" rx="9" ry="0.6" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="20" cy="19.5" rx="0.6" ry="9" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="20" cy="19.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(45 20 19.5)" />
              <ellipse cx="20" cy="19.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(-45 20 19.5)" />
            </g>
            {/* Stator hub */}
            <circle cx="20" cy="19.5" r="2.2" fill="#0f172a" stroke="#334155" strokeWidth="0.3" />
            <circle cx="20" cy="19.5" r="1" fill="#1e293b" />
            <circle cx="20" cy="19.5" r="0.4" fill="#475569" />
            {/* Mount bolt */}
            <circle cx="22" cy="19.5" r="0.4" fill="#64748b" />
            {/* Green nav LED at tip */}
            <circle cx="13" cy="13" r="1.4" fill="#86efac" opacity="0.4" className="animate-pulse" />
            <circle cx="13" cy="13" r="0.7" fill="#4ade80" className="animate-pulse" />
          </g>

          {/* Front-right rotor */}
          <g>
            <circle cx="100" cy="19.5" r="11" fill="url(#rotorRim)" />
            <circle cx="100" cy="19.5" r="9.5" fill="url(#rotorBlur)" />
            <ellipse cx="100" cy="19.5" rx="9" ry="9" fill="none" stroke="rgba(125,211,252,0.25)" strokeWidth="0.3" />
            <g className="animate-spin" style={{ transformOrigin: '100px 19.5px', animationDuration: '0.22s', animationDirection: 'reverse' }}>
              <ellipse cx="100" cy="19.5" rx="9" ry="0.6" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="100" cy="19.5" rx="0.6" ry="9" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="100" cy="19.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(45 100 19.5)" />
              <ellipse cx="100" cy="19.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(-45 100 19.5)" />
            </g>
            <circle cx="100" cy="19.5" r="2.2" fill="#0f172a" stroke="#334155" strokeWidth="0.3" />
            <circle cx="100" cy="19.5" r="1" fill="#1e293b" />
            <circle cx="100" cy="19.5" r="0.4" fill="#475569" />
            <circle cx="98" cy="19.5" r="0.4" fill="#64748b" />
            <circle cx="107" cy="13" r="1.4" fill="#86efac" opacity="0.4" className="animate-pulse" />
            <circle cx="107" cy="13" r="0.7" fill="#4ade80" className="animate-pulse" />
          </g>

          {/* Rear-left rotor */}
          <g>
            <circle cx="20" cy="60.5" r="11" fill="url(#rotorRim)" />
            <circle cx="20" cy="60.5" r="9.5" fill="url(#rotorBlur)" />
            <ellipse cx="20" cy="60.5" rx="9" ry="9" fill="none" stroke="rgba(125,211,252,0.25)" strokeWidth="0.3" />
            <g className="animate-spin" style={{ transformOrigin: '20px 60.5px', animationDuration: '0.24s', animationDirection: 'reverse' }}>
              <ellipse cx="20" cy="60.5" rx="9" ry="0.6" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="20" cy="60.5" rx="0.6" ry="9" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="20" cy="60.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(45 20 60.5)" />
              <ellipse cx="20" cy="60.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(-45 20 60.5)" />
            </g>
            <circle cx="20" cy="60.5" r="2.2" fill="#0f172a" stroke="#334155" strokeWidth="0.3" />
            <circle cx="20" cy="60.5" r="1" fill="#1e293b" />
            <circle cx="20" cy="60.5" r="0.4" fill="#475569" />
            <circle cx="22" cy="60.5" r="0.4" fill="#64748b" />
            <circle cx="13" cy="67" r="1.4" fill="#fca5a5" opacity="0.4" className="animate-pulse" style={{ animationDuration: '1.6s' }} />
            <circle cx="13" cy="67" r="0.7" fill="#f87171" className="animate-pulse" style={{ animationDuration: '1.6s' }} />
          </g>

          {/* Rear-right rotor */}
          <g>
            <circle cx="100" cy="60.5" r="11" fill="url(#rotorRim)" />
            <circle cx="100" cy="60.5" r="9.5" fill="url(#rotorBlur)" />
            <ellipse cx="100" cy="60.5" rx="9" ry="9" fill="none" stroke="rgba(125,211,252,0.25)" strokeWidth="0.3" />
            <g className="animate-spin" style={{ transformOrigin: '100px 60.5px', animationDuration: '0.26s' }}>
              <ellipse cx="100" cy="60.5" rx="9" ry="0.6" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="100" cy="60.5" rx="0.6" ry="9" fill="rgba(148,163,184,0.35)" />
              <ellipse cx="100" cy="60.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(45 100 60.5)" />
              <ellipse cx="100" cy="60.5" rx="8.5" ry="1.2" fill="rgba(125,211,252,0.10)" transform="rotate(-45 100 60.5)" />
            </g>
            <circle cx="100" cy="60.5" r="2.2" fill="#0f172a" stroke="#334155" strokeWidth="0.3" />
            <circle cx="100" cy="60.5" r="1" fill="#1e293b" />
            <circle cx="100" cy="60.5" r="0.4" fill="#475569" />
            <circle cx="98" cy="60.5" r="0.4" fill="#64748b" />
            <circle cx="107" cy="67" r="1.4" fill="#fca5a5" opacity="0.4" className="animate-pulse" style={{ animationDuration: '1.6s' }} />
            <circle cx="107" cy="67" r="0.7" fill="#f87171" className="animate-pulse" style={{ animationDuration: '1.6s' }} />
          </g>
        </svg>
      </div>

      {/* HUD: Corner brackets */}
      <div className="absolute top-2 left-2 h-3 w-3 border-l border-t border-sky-300/70" />
      <div className="absolute top-2 right-2 h-3 w-3 border-r border-t border-sky-300/70" />
      <div className="absolute bottom-2 left-2 h-3 w-3 border-l border-b border-sky-300/70" />
      <div className="absolute bottom-2 right-2 h-3 w-3 border-r border-b border-sky-300/70" />

      {/* Top-left telemetry */}
      <div className="absolute top-2.5 left-5 text-[6px] leading-[1.5] tabular-nums">
        <div className="flex gap-1.5 items-center mb-0.5">
          <span className="h-1 w-1 rounded-full bg-emerald-300 animate-pulse" />
          <span className="text-emerald-300 tracking-[0.15em] text-[5.5px]">LIVE · CAM-01</span>
        </div>
        <div className="text-sky-300/90"><span className="text-sky-300/50 mr-1.5">ALT</span><span className="text-sky-200">142.3</span><span className="text-sky-300/50 ml-0.5">m</span></div>
        <div className="text-sky-300/90"><span className="text-sky-300/50 mr-1.5">SPD</span><span className="text-sky-200">24.0</span><span className="text-sky-300/50 ml-0.5">m/s</span></div>
        <div className="text-sky-300/90"><span className="text-sky-300/50 mr-1.5">HDG</span><span className="text-sky-200">087°</span><span className="text-sky-300/50 ml-0.5">NE</span></div>
        <div className="text-sky-300/90"><span className="text-sky-300/50 mr-1.5">PTC</span><span className="text-sky-200">-02.4°</span></div>
      </div>

      {/* Top-right telemetry */}
      <div className="absolute top-2.5 right-5 text-[6px] leading-[1.5] tabular-nums text-right">
        <div className="flex gap-1.5 items-center justify-end mb-0.5">
          <span className="text-amber-300 tracking-[0.15em] text-[5.5px]">REC 00:12:47</span>
          <span className="h-1 w-1 rounded-full bg-red-400 animate-pulse" />
        </div>
        <div className="text-sky-300/90 flex items-center justify-end gap-1.5">
          <span className="text-sky-300/50">BAT</span>
          <span className="inline-flex items-center gap-0.5">
            <span className="inline-block h-1 w-3 border border-sky-300/60 relative">
              <span className="absolute inset-y-0 left-0 w-[87%] bg-emerald-300/80" />
            </span>
            <span className="text-emerald-200">87%</span>
          </span>
        </div>
        <div className="text-sky-300/90"><span className="text-sky-300/50 mr-1.5">SAT</span><span className="text-sky-200">12</span><span className="text-emerald-300 ml-0.5">●</span></div>
        <div className="text-sky-300/90"><span className="text-sky-300/50 mr-1.5">LINK</span><span className="text-sky-200">5.8</span><span className="text-sky-300/50 ml-0.5">GHz</span></div>
        <div className="text-sky-300/90"><span className="text-sky-300/50 mr-1.5">SIG</span><span className="text-sky-200">-62</span><span className="text-sky-300/50 ml-0.5">dBm</span></div>
      </div>

      {/* Bottom-left: AGL vertical bar */}
      <div className="absolute bottom-5 left-5 flex items-end gap-1">
        <div className="text-[5.5px] text-sky-300/60 tracking-[0.15em] -rotate-90 origin-bottom-left translate-x-1 -translate-y-1">AGL</div>
        <div className="relative h-14 w-1 bg-sky-300/10 border border-sky-300/30">
          <div className="absolute inset-x-0 bottom-0 h-[68%] bg-gradient-to-t from-sky-400/80 to-cyan-300/80" />
          <div className="absolute -right-2 top-[32%] flex items-center gap-0.5">
            <span className="h-px w-1.5 bg-sky-300" />
          </div>
          {/* Ticks */}
          <span className="absolute -left-1.5 top-0 h-px w-1 bg-sky-300/50" />
          <span className="absolute -left-1.5 top-1/4 h-px w-1 bg-sky-300/30" />
          <span className="absolute -left-1.5 top-1/2 h-px w-1 bg-sky-300/30" />
          <span className="absolute -left-1.5 top-3/4 h-px w-1 bg-sky-300/30" />
          <span className="absolute -left-1.5 bottom-0 h-px w-1 bg-sky-300/50" />
        </div>
        <div className="flex flex-col text-[5px] text-sky-300/60 h-14 justify-between leading-none">
          <span>200</span>
          <span>150</span>
          <span>100</span>
          <span>050</span>
          <span>000</span>
        </div>
      </div>

      {/* Bottom-right: mode indicator */}
      <div className="absolute bottom-5 right-5 text-[5.5px] text-right leading-[1.5] tabular-nums">
        <div className="flex items-center justify-end gap-1 text-sky-300/60 tracking-[0.15em] mb-0.5">MODE</div>
        <div className="text-sky-200 text-[6.5px] tracking-[0.2em]">AUTO · WAYPT</div>
        <div className="flex items-center justify-end gap-1 mt-1">
          <span className="text-sky-300/50">WP</span>
          <span className="text-sky-200">04 / 12</span>
        </div>
        <div className="flex items-center justify-end gap-1">
          <span className="text-sky-300/50">ETA</span>
          <span className="text-sky-200">02:14</span>
        </div>
      </div>

      {/* Bottom-center: GPS coords */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 text-[6px] tabular-nums whitespace-nowrap">
        <span className="text-sky-300/60 tracking-[0.15em]">GPS</span>
        <span className="text-sky-200">37.7749°N · 122.4194°W</span>
        <span className="text-emerald-300 tracking-[0.1em] flex items-center gap-0.5">
          <span className="inline-block h-1 w-1 rounded-full bg-emerald-300 animate-pulse" />
          RTK FIX
        </span>
      </div>

      {/* Faint scanlines for camera-feed look */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent 0, transparent 2px, #7dd3fc 2px, #7dd3fc 3px)' }} />
    </div>
  );
}

/* ─────────────  Scene 4: IoT  ───────────── */
function IoTScene() {
  return (
    <div className="relative h-full w-full overflow-hidden" style={{ background: '#0e2820' }}>
      {/* PCB fiberglass texture */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 1px, transparent 1px, transparent 4px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 4px)',
        }}
      />
      {/* Solder-mask gloss radial highlight */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 25% 15%, rgba(252,211,77,0.10), transparent 60%), radial-gradient(ellipse 50% 60% at 80% 90%, rgba(0,0,0,0.45), transparent 60%)',
        }}
      />
      {/* PCB edge border */}
      <div className="absolute inset-0 border" style={{ borderColor: '#1d4a3a' }} />

      {/* SVG: copper traces, pads, vias, pulses */}
      <svg
        viewBox="0 0 377 190"
        className="absolute inset-0 h-full w-full"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <filter id="pulseGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="copper" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#d97706" />
            <stop offset="50%" stopColor="#b45309" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
          <linearGradient id="copperHot" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
          <radialGradient id="padGrad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#fcd34d" />
            <stop offset="70%" stopColor="#d97706" />
            <stop offset="100%" stopColor="#78350f" />
          </radialGradient>

          {/* Trace paths used by both stroke render and motion path */}
          <path id="trace1" d="M 188 95 L 188 50 L 240 50 L 260 30" />
          <path id="trace2" d="M 213 95 L 280 95 L 300 75" />
          <path id="trace3" d="M 213 110 L 250 110 L 270 130 L 320 130" />
          <path id="trace4" d="M 188 120 L 188 150 L 140 150 L 120 170" />
          <path id="trace5" d="M 163 110 L 90 110 L 70 90" />
          <path id="trace6" d="M 163 95 L 130 95 L 110 75 L 60 75" />
          <path id="trace7" d="M 175 95 L 175 60 L 130 60 L 110 40 L 50 40" />
          <path id="trace8" d="M 200 95 L 200 35 L 230 35" />
          <path id="trace9" d="M 213 102 L 340 102" />
          <path id="trace10" d="M 188 110 L 188 130 L 160 130" />
          <path id="trace11" d="M 200 120 L 200 160 L 240 160 L 260 175" />
          <path id="trace12" d="M 175 110 L 175 145 L 90 145" />
        </defs>

        {/* Background bus traces (thinner, more distant) */}
        <g stroke="#7c2d12" strokeWidth="0.5" fill="none" opacity="0.55">
          <path d="M 10 20 L 360 20" />
          <path d="M 10 180 L 360 180" />
          <path d="M 15 25 L 15 175" />
          <path d="M 362 25 L 362 175" />
          <path d="M 30 10 L 30 25" />
          <path d="M 50 10 L 50 25" />
          <path d="M 340 10 L 340 25" />
          <path d="M 340 175 L 340 188" />
        </g>

        {/* Power/ground plane hatching hint */}
        <g stroke="#a16207" strokeWidth="0.3" opacity="0.25">
          <path d="M 8 8 L 20 8 L 20 20" fill="none" />
          <path d="M 357 8 L 369 8 L 369 20" fill="none" />
        </g>

        {/* Main copper traces */}
        <g stroke="url(#copper)" strokeWidth="1.2" fill="none" strokeLinecap="square">
          <use href="#trace1" />
          <use href="#trace2" />
          <use href="#trace3" />
          <use href="#trace4" />
          <use href="#trace5" />
          <use href="#trace6" />
          <use href="#trace7" />
          <use href="#trace8" />
          <use href="#trace10" />
          <use href="#trace11" />
          <use href="#trace12" />
        </g>

        {/* Active signal path — brighter gold */}
        <use
          href="#trace9"
          stroke="url(#copperHot)"
          strokeWidth="1.4"
          fill="none"
          style={{ filter: 'drop-shadow(0 0 1.5px #fbbf24)' }}
        />

        {/* Pads at trace endpoints */}
        {[
          [260, 30], [300, 75], [320, 130], [120, 170], [70, 90], [60, 75],
          [50, 40], [230, 35], [340, 102], [160, 130], [260, 175], [90, 145],
        ].map(([cx, cy], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r="2.2" fill="url(#padGrad)" />
            <circle cx={cx} cy={cy} r="0.7" fill="#1c1917" />
          </g>
        ))}

        {/* Vias along traces */}
        {[
          [188, 50], [240, 50], [280, 95], [250, 110], [270, 130], [188, 150],
          [140, 150], [90, 110], [130, 95], [110, 75], [175, 60], [130, 60],
          [200, 35], [188, 130], [200, 160], [175, 145],
        ].map(([cx, cy], i) => (
          <g key={`v${i}`}>
            <circle cx={cx} cy={cy} r="1.2" fill="#b45309" />
            <circle cx={cx} cy={cy} r="0.45" fill="#1c1917" />
          </g>
        ))}

        {/* Resistor pads (silver caps suggested via small rects on traces) */}
        {/* R1 on trace2 */}
        <g>
          <rect x="232" y="92" width="8" height="6" rx="0.5" fill="#0f172a" stroke="#475569" strokeWidth="0.3" />
          <rect x="231.5" y="92" width="1.5" height="6" fill="#cbd5e1" />
          <rect x="239" y="92" width="1.5" height="6" fill="#cbd5e1" />
          <text x="236" y="96.3" textAnchor="middle" fill="#f1f5f9" fontSize="2.8" fontFamily="ui-monospace, monospace">4701</text>
        </g>
        {/* R2 on trace5 */}
        <g>
          <rect x="100" y="107" width="8" height="6" rx="0.5" fill="#0f172a" stroke="#475569" strokeWidth="0.3" />
          <rect x="99.5" y="107" width="1.5" height="6" fill="#cbd5e1" />
          <rect x="107" y="107" width="1.5" height="6" fill="#cbd5e1" />
          <text x="104" y="111.3" textAnchor="middle" fill="#f1f5f9" fontSize="2.8" fontFamily="ui-monospace, monospace">100R</text>
        </g>
        {/* R3 on trace7 */}
        <g>
          <rect x="78" y="37" width="8" height="6" rx="0.5" fill="#0f172a" stroke="#475569" strokeWidth="0.3" />
          <rect x="77.5" y="37" width="1.5" height="6" fill="#cbd5e1" />
          <rect x="85" y="37" width="1.5" height="6" fill="#cbd5e1" />
          <text x="82" y="41.3" textAnchor="middle" fill="#f1f5f9" fontSize="2.8" fontFamily="ui-monospace, monospace">2K20</text>
        </g>
        {/* R4 on trace11 */}
        <g>
          <rect x="218" y="157" width="8" height="6" rx="0.5" fill="#0f172a" stroke="#475569" strokeWidth="0.3" />
          <rect x="217.5" y="157" width="1.5" height="6" fill="#cbd5e1" />
          <rect x="225" y="157" width="1.5" height="6" fill="#cbd5e1" />
          <text x="222" y="161.3" textAnchor="middle" fill="#f1f5f9" fontSize="2.8" fontFamily="ui-monospace, monospace">330R</text>
        </g>

        {/* Ceramic caps (small dark rectangles) */}
        <g>
          <rect x="156" y="91" width="6" height="4" rx="0.4" fill="#451a03" stroke="#78350f" strokeWidth="0.3" />
        </g>
        <g>
          <rect x="214" y="118" width="6" height="4" rx="0.4" fill="#451a03" stroke="#78350f" strokeWidth="0.3" />
        </g>
        <g>
          <rect x="186" y="74" width="4" height="6" rx="0.4" fill="#451a03" stroke="#78350f" strokeWidth="0.3" />
        </g>

        {/* Electrolytic cap (blue cylinder with cross silkscreen) */}
        <g transform="translate(40, 130)">
          <circle cx="0" cy="0" r="11" fill="#1e3a8a" stroke="#1e40af" strokeWidth="0.5" />
          <circle cx="0" cy="0" r="9" fill="none" stroke="#3b82f6" strokeWidth="0.4" opacity="0.7" />
          <circle cx="0" cy="0" r="6" fill="none" stroke="#60a5fa" strokeWidth="0.3" opacity="0.5" />
          <path d="M -5 0 L 5 0 M 0 -5 L 0 5" stroke="#dbeafe" strokeWidth="0.5" />
          <text x="0" y="16" textAnchor="middle" fill="#fef3c7" fontSize="3.5" fontFamily="ui-monospace, monospace">C5 470uF</text>
        </g>

        {/* Crystal oscillator (silver pill) */}
        <g transform="translate(310, 50)">
          <rect x="-12" y="-4" width="24" height="8" rx="3.5" fill="#94a3b8" stroke="#475569" strokeWidth="0.4" />
          <rect x="-11" y="-3" width="22" height="6" rx="3" fill="none" stroke="#e2e8f0" strokeWidth="0.3" opacity="0.6" />
          <text x="0" y="0.8" textAnchor="middle" fill="#0f172a" fontSize="3.2" fontFamily="ui-monospace, monospace" fontWeight="700">16.000 MHz</text>
          <text x="0" y="9" textAnchor="middle" fill="#fef3c7" fontSize="3" fontFamily="ui-monospace, monospace">Y1</text>
        </g>

        {/* SMD LEDs */}
        <g>
          {/* Red LED1 */}
          <rect x="345" y="125" width="6" height="3" rx="0.3" fill="#7f1d1d" stroke="#cbd5e1" strokeWidth="0.3" />
          <rect x="346.5" y="125.5" width="3" height="2" fill="#ef4444" className="animate-pulse" />
          <text x="348" y="133" textAnchor="middle" fill="#fef3c7" fontSize="3" fontFamily="ui-monospace, monospace">LED1</text>
        </g>
        <g>
          {/* Green LED2 */}
          <rect x="115" y="167" width="6" height="3" rx="0.3" fill="#14532d" stroke="#cbd5e1" strokeWidth="0.3" />
          <rect x="116.5" y="167.5" width="3" height="2" fill="#22c55e" className="animate-pulse" style={{ animationDelay: '0.4s' }} />
          <text x="118" y="175" textAnchor="middle" fill="#fef3c7" fontSize="3" fontFamily="ui-monospace, monospace">LED2</text>
        </g>
        <g>
          {/* Amber LED3 */}
          <rect x="46" y="36" width="6" height="3" rx="0.3" fill="#78350f" stroke="#cbd5e1" strokeWidth="0.3" />
          <rect x="47.5" y="36.5" width="3" height="2" fill="#fbbf24" className="animate-pulse" style={{ animationDelay: '0.8s' }} />
          <text x="49" y="44" textAnchor="middle" fill="#fef3c7" fontSize="3" fontFamily="ui-monospace, monospace">LED3</text>
        </g>

        {/* Silkscreen component labels */}
        <g fill="#fef3c7" fontFamily="ui-monospace, monospace" opacity="0.85">
          <text x="236" y="89" textAnchor="middle" fontSize="3">R1</text>
          <text x="104" y="104" textAnchor="middle" fontSize="3">R2</text>
          <text x="82" y="34" textAnchor="middle" fontSize="3">R3</text>
          <text x="222" y="154" textAnchor="middle" fontSize="3">R4</text>
          <text x="159" y="89" textAnchor="middle" fontSize="3">C1 100nF</text>
          <text x="217" y="116" textAnchor="middle" fontSize="3">C2 10uF</text>
          <text x="188" y="72" textAnchor="middle" fontSize="3">C3</text>
        </g>

        {/* Central MCU (drawn in SVG for crisp scaling) */}
        <g transform="translate(188, 102.5)">
          {/* Shadow */}
          <rect x="-27" y="-25" width="54" height="52" rx="4" fill="#000" opacity="0.5" />
          {/* IC body */}
          <rect x="-25" y="-23" width="50" height="48" rx="3" fill="#0f172a" stroke="#1e293b" strokeWidth="0.5" />
          {/* Inner highlight (overhead light) */}
          <rect
            x="-25"
            y="-23"
            width="50"
            height="48"
            rx="3"
            fill="url(#chipHighlight)"
            opacity="0.6"
          />
          <defs>
            <linearGradient id="chipHighlight" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#334155" stopOpacity="0.6" />
              <stop offset="40%" stopColor="#1e293b" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.4" />
            </linearGradient>
          </defs>

          {/* Pin 1 dot */}
          <circle cx="-21" cy="-19" r="1.2" fill="#fef3c7" />

          {/* Silicon die rectangle */}
          <rect x="-15" y="-13" width="30" height="28" rx="0.5" fill="#020617" stroke="#1e293b" strokeWidth="0.3" />
          {/* Die internal traces */}
          <g stroke="#475569" strokeWidth="0.2" opacity="0.6" fill="none">
            <path d="M -13 -10 L 13 -10" />
            <path d="M -13 -6 L 13 -6" />
            <path d="M -13 -2 L 13 -2" />
            <path d="M -13 2 L 13 2" />
            <path d="M -13 6 L 13 6" />
            <path d="M -13 10 L 13 10" />
            <path d="M -10 -13 L -10 13" />
            <path d="M -4 -13 L -4 13" />
            <path d="M 2 -13 L 2 13" />
            <path d="M 8 -13 L 8 13" />
          </g>

          {/* Body label */}
          <text x="0" y="-3" textAnchor="middle" fill="#e2e8f0" fontSize="3.6" fontFamily="ui-monospace, monospace" fontWeight="600">STX-32M2</text>
          <text x="0" y="2" textAnchor="middle" fill="#94a3b8" fontSize="2.6" fontFamily="ui-monospace, monospace">SPX 2624A</text>
          <text x="0" y="6.5" textAnchor="middle" fill="#64748b" fontSize="2.2" fontFamily="ui-monospace, monospace">LQFP48</text>

          {/* Green status LED on the chip */}
          <circle cx="10" cy="10" r="1.3" fill="#16a34a" />
          <circle cx="10" cy="10" r="1.3" fill="#4ade80" className="animate-pulse" />

          {/* Pin pads — top */}
          {Array.from({ length: 11 }).map((_, i) => (
            <g key={`pt${i}`}>
              <rect x={-22 + i * 4.2} y={-25} width="2.2" height="3.5" fill="#cbd5e1" />
              <rect x={-22 + i * 4.2} y={-22} width="2.2" height="1.5" fill="#94a3b8" />
            </g>
          ))}
          {/* Pin pads — bottom */}
          {Array.from({ length: 11 }).map((_, i) => (
            <g key={`pb${i}`}>
              <rect x={-22 + i * 4.2} y={21.5} width="2.2" height="3.5" fill="#cbd5e1" />
              <rect x={-22 + i * 4.2} y={20.5} width="2.2" height="1.5" fill="#94a3b8" />
            </g>
          ))}
          {/* Pin pads — left */}
          {Array.from({ length: 11 }).map((_, i) => (
            <g key={`pl${i}`}>
              <rect x={-25} y={-20 + i * 4.1} width="3.5" height="2.2" fill="#cbd5e1" />
              <rect x={-22} y={-20 + i * 4.1} width="1.5" height="2.2" fill="#94a3b8" />
            </g>
          ))}
          {/* Pin pads — right */}
          {Array.from({ length: 11 }).map((_, i) => (
            <g key={`pr${i}`}>
              <rect x={21.5} y={-20 + i * 4.1} width="3.5" height="2.2" fill="#cbd5e1" />
              <rect x={21.5} y={-20 + i * 4.1} width="1.5" height="2.2" fill="#94a3b8" />
            </g>
          ))}

          {/* Pin numbers / abbreviations silkscreen (selected) */}
          <g fill="#fef3c7" fontFamily="ui-monospace, monospace" fontSize="1.8" opacity="0.85">
            <text x="-21" y="-26" textAnchor="middle">1</text>
            <text x="-12.5" y="-26" textAnchor="middle">VCC</text>
            <text x="0" y="-26" textAnchor="middle">RST</text>
            <text x="12.5" y="-26" textAnchor="middle">GND</text>
            <text x="20.7" y="-26" textAnchor="middle">12</text>
            <text x="-21" y="28.5" textAnchor="middle">24</text>
            <text x="-12.5" y="28.5" textAnchor="middle">TX</text>
            <text x="0" y="28.5" textAnchor="middle">RX</text>
            <text x="12.5" y="28.5" textAnchor="middle">SDA</text>
            <text x="20.7" y="28.5" textAnchor="middle">SCL</text>
          </g>
        </g>

        {/* "U1 STX-32M2" silkscreen near chip */}
        <text x="188" y="138" textAnchor="middle" fill="#fef3c7" fontSize="3.2" fontFamily="ui-monospace, monospace" opacity="0.8">U1 STX-32M2</text>

        {/* + power rail marker */}
        <g transform="translate(20, 50)">
          <text fill="#fef3c7" fontSize="6" fontFamily="ui-monospace, monospace" fontWeight="700">+</text>
          <text x="6" y="-1" fill="#fef3c7" fontSize="3" fontFamily="ui-monospace, monospace" opacity="0.8">3V3</text>
        </g>
        <g transform="translate(20, 165)">
          <rect x="0" y="-4" width="6" height="1" fill="#fef3c7" />
          <rect x="0" y="-2.2" width="6" height="0.6" fill="#fef3c7" />
          <text x="9" y="-1.5" fill="#fef3c7" fontSize="3" fontFamily="ui-monospace, monospace" opacity="0.8">GND</text>
        </g>

        {/* Board revision label */}
        <g transform="translate(335, 18)">
          <text textAnchor="end" fill="#fef3c7" fontSize="3.2" fontFamily="ui-monospace, monospace" opacity="0.85">SPX-MAIN-REV.B</text>
          <text x="0" y="4" textAnchor="end" fill="#fef3c7" fontSize="2.6" fontFamily="ui-monospace, monospace" opacity="0.6">© 2026 SPECTRANOX</text>
        </g>

        {/* Tiny test points */}
        <g>
          {[[355, 60], [355, 80], [355, 100]].map(([cx, cy], i) => (
            <g key={`tp${i}`}>
              <circle cx={cx} cy={cy} r="1.8" fill="url(#padGrad)" />
              <circle cx={cx} cy={cy} r="0.5" fill="#1c1917" />
              <text x={cx - 4} y={cy + 1} textAnchor="end" fill="#fef3c7" fontSize="2.4" fontFamily="ui-monospace, monospace" opacity="0.7">TP{i + 1}</text>
            </g>
          ))}
        </g>

        {/* Data pulses traveling along traces */}
        <g style={{ filter: 'drop-shadow(0 0 2px #fbbf24)' }}>
          <circle r="1.6" fill="#fde68a">
            <animateMotion dur="1.8s" repeatCount="indefinite" rotate="auto">
              <mpath href="#trace1" />
            </animateMotion>
          </circle>
          <circle r="1.4" fill="#fcd34d">
            <animateMotion dur="2.4s" repeatCount="indefinite" rotate="auto">
              <mpath href="#trace3" />
            </animateMotion>
          </circle>
          <circle r="1.6" fill="#fde68a">
            <animateMotion dur="1.2s" repeatCount="indefinite" rotate="auto">
              <mpath href="#trace9" />
            </animateMotion>
          </circle>
          <circle r="1.3" fill="#fbbf24">
            <animateMotion dur="2.1s" repeatCount="indefinite" rotate="auto">
              <mpath href="#trace5" />
            </animateMotion>
          </circle>
          <circle r="1.4" fill="#fcd34d">
            <animateMotion dur="2.7s" repeatCount="indefinite" rotate="auto">
              <mpath href="#trace7" />
            </animateMotion>
          </circle>
          <circle r="1.5" fill="#fde68a">
            <animateMotion dur="1.6s" repeatCount="indefinite" rotate="auto">
              <mpath href="#trace11" />
            </animateMotion>
          </circle>
          <circle r="1.3" fill="#fbbf24">
            <animateMotion dur="2.0s" repeatCount="indefinite" rotate="auto">
              <mpath href="#trace2" />
            </animateMotion>
          </circle>
        </g>

        {/* Trailing faint pulse on hot trace */}
        <circle r="0.8" fill="#fef3c7" opacity="0.6">
          <animateMotion dur="1.2s" repeatCount="indefinite" begin="0.15s">
            <mpath href="#trace9" />
          </animateMotion>
        </circle>
      </svg>

      {/* Warm overhead light tint */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 15% 10%, rgba(252, 211, 77, 0.10), transparent 55%)',
        }}
      />

      {/* Subtle scanline / gloss */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 25%, transparent 70%, rgba(0,0,0,0.25) 100%)',
        }}
      />

      {/* Status overlay HUD pill */}
      <div className="absolute right-2 bottom-2 flex items-center gap-1.5 rounded-sm border border-amber-500/30 bg-black/40 px-1.5 py-0.5 backdrop-blur-[1px]">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-300" />
        </span>
        <span className="font-mono text-[6px] tabular-nums tracking-wider text-amber-200">
          MCU.LINK 16.000MHz · 3V3 OK
        </span>
      </div>

      {/* Corner mark */}
      <div className="absolute left-2 top-2 font-mono text-[6px] tracking-widest text-amber-200/70">
        IoT · PCB BUS
      </div>
    </div>
  );
}
/* ─────────────  Scene 5: SOCIAL & GROWTH  ───────────── */
function GrowthScene() {
  const creators = [
    {
      handle: "@maya_kapoor",
      name: "Maya K.",
      niche: "lifestyle",
      followers: "892K",
      growth: "+8.2K",
      eng: "6.4%",
      platform: "FD",
      platformBg: "from-pink-500/30 to-rose-500/30",
      platformBorder: "border-pink-400/40",
      platformText: "text-pink-200",
      avatarFrom: "#fda4af",
      avatarTo: "#9d174d",
      offset: 0,
      z: 10,
    },
    {
      handle: "@nova.creates",
      name: "Nova R.",
      niche: "fashion",
      followers: "2.4M",
      growth: "+12.4K",
      eng: "8.2%",
      platform: "ST",
      platformBg: "from-fuchsia-500/30 to-rose-500/30",
      platformBorder: "border-fuchsia-400/40",
      platformText: "text-fuchsia-200",
      avatarFrom: "#f0abfc",
      avatarTo: "#86198f",
      offset: 1,
      z: 30,
      live: true,
    },
    {
      handle: "@atlas.studio",
      name: "Atlas Wei",
      niche: "design",
      followers: "1.2M",
      growth: "+4.8K",
      eng: "5.1%",
      platform: "VD",
      platformBg: "from-rose-500/30 to-amber-500/20",
      platformBorder: "border-rose-400/40",
      platformText: "text-rose-200",
      avatarFrom: "#fed7aa",
      avatarTo: "#9a3412",
      offset: 0,
      z: 20,
    },
  ];

  const feed = [
    { dot: "bg-rose-400", text: "+847 likes on Reel #142", time: "2s", hi: true },
    { dot: "bg-emerald-400", text: "12 new followers", time: "5s" },
    { dot: "bg-amber-300", text: "+34 comments on post", time: "11s" },
    { dot: "bg-fuchsia-400", text: "+128 shares · Story 7", time: "18s" },
    { dot: "bg-rose-400", text: "Story 4.2K views", time: "24s" },
  ];

  const Avatar = ({ from, to, id }) => (
    <svg viewBox="0 0 24 24" className="h-6 w-6">
      <defs>
        <radialGradient id={`av-${id}`} cx="0.3" cy="0.3" r="0.9">
          <stop offset="0%" stopColor={from} />
          <stop offset="100%" stopColor={to} />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="12" fill={`url(#av-${id})`} />
      <circle cx="12" cy="10" r="3.2" fill="rgba(255,255,255,0.85)" />
      <path d="M5 22 Q12 14 19 22 Z" fill="rgba(255,255,255,0.75)" />
    </svg>
  );

  const Check = () => (
    <svg viewBox="0 0 10 10" className="h-2 w-2">
      <circle cx="5" cy="5" r="5" fill="#f43f5e" />
      <path d="M2.8 5.2 L4.3 6.6 L7.2 3.6" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const Spark = ({ up }) => (
    <svg viewBox="0 0 40 12" className="h-3 w-10">
      <polyline
        points={up ? "0,10 6,8 12,9 18,6 24,7 30,3 36,4 40,1" : "0,8 6,9 12,6 18,7 24,4 30,5 36,2 40,3"}
        fill="none"
        stroke="#fb7185"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0f1e] font-mono text-white">
      {/* radial vignette */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(244,63,94,0.18), transparent 55%), radial-gradient(ellipse at 85% 90%, rgba(217,70,239,0.14), transparent 60%)",
        }}
      />

      {/* floating dots */}
      <div className="pointer-events-none absolute inset-0">
        <span className="absolute left-[12%] top-[22%] h-1 w-1 rounded-full bg-rose-400/40 animate-ping" />
        <span className="absolute left-[68%] top-[14%] h-1 w-1 rounded-full bg-fuchsia-400/40 animate-ping" style={{ animationDelay: "0.7s" }} />
        <span className="absolute left-[42%] top-[78%] h-1 w-1 rounded-full bg-pink-400/40 animate-ping" style={{ animationDelay: "1.4s" }} />
        <span className="absolute left-[88%] top-[55%] h-1 w-1 rounded-full bg-rose-400/40 animate-ping" style={{ animationDelay: "2.1s" }} />
      </div>

      {/* top label */}
      <div className="absolute left-2 top-1.5 flex items-center gap-1 text-[6px] tracking-[0.18em] text-rose-300/80">
        <span>▸</span>
        <span>SPECTRANOX · CREATOR DESK</span>
      </div>

      {/* top right badge */}
      <div className="absolute right-2 top-1.5 flex items-center gap-1 rounded-sm border border-rose-400/30 bg-rose-500/10 px-1 py-[1px] text-[5px] tracking-[0.18em] text-rose-200">
        <span className="h-1 w-1 rounded-full bg-emerald-400 animate-pulse" />
        <span>MANAGING 24 ACCOUNTS</span>
      </div>

      {/* main grid */}
      <div className="absolute inset-x-2 top-6 bottom-2 flex gap-2">
        {/* LEFT COLUMN — creator cards */}
        <div className="relative flex-1" style={{ flexBasis: "58%" }}>
          {creators.map((c, i) => {
            const isLive = c.live;
            const top = 4 + i * 50;
            return (
              <div
                key={i}
                className="absolute left-0 right-0"
                style={{
                  top: `${top}px`,
                  transform: `translateX(${c.offset * 4}px)`,
                  zIndex: c.z,
                }}
              >
                {isLive && (
                  <div className="pointer-events-none absolute -inset-[2px] rounded-md border border-rose-400/60 animate-pulse"
                    style={{ boxShadow: "0 0 12px rgba(244,63,94,0.45), inset 0 0 8px rgba(244,63,94,0.2)" }}
                  />
                )}
                <div
                  className="relative flex items-center gap-2 rounded-md border border-rose-400/20 px-2 py-1.5 backdrop-blur"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(159,18,57,0.28), rgba(15,23,42,0.7))",
                  }}
                >
                  {/* avatar */}
                  <div className="relative flex-shrink-0">
                    <Avatar from={c.avatarFrom} to={c.avatarTo} id={i} />
                    <span className="absolute -bottom-[1px] -right-[1px] h-1.5 w-1.5 rounded-full bg-emerald-400 ring-[1.5px] ring-[#1a0a1a]" />
                  </div>

                  {/* middle */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[7px] font-bold text-white truncate">{c.handle}</span>
                      <Check />
                      {isLive && (
                        <span className="ml-0.5 flex items-center gap-0.5 rounded-sm bg-rose-500/30 px-1 py-[0.5px] text-[4.5px] font-bold tracking-wider text-rose-100">
                          <span className="h-[3px] w-[3px] rounded-full bg-rose-300 animate-pulse" />
                          LIVE
                        </span>
                      )}
                    </div>
                    <div className="text-[6px] text-slate-300">{c.name}</div>
                    <div className="text-[5px] text-slate-400/70 truncate">
                      {c.niche} · {c.followers} followers
                    </div>
                  </div>

                  {/* right */}
                  <div className="flex flex-shrink-0 flex-col items-end gap-[1px]">
                    <span
                      className={`rounded-sm border ${c.platformBorder} bg-gradient-to-br ${c.platformBg} px-1 py-[0.5px] text-[5px] font-bold tracking-widest ${c.platformText}`}
                    >
                      {c.platform}
                    </span>
                    <span className="text-[8px] font-bold tabular-nums text-white">{c.followers}</span>
                    <span className="flex items-center gap-0.5 text-[5.5px] tabular-nums text-emerald-400">
                      <span>{c.growth}</span>
                      <svg viewBox="0 0 6 6" className="h-1.5 w-1.5"><path d="M3 1 L5 5 L1 5 Z" fill="#34d399" /></svg>
                    </span>
                    <span className="text-[5px] tabular-nums text-rose-200/70">ENG {c.eng}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col gap-1.5" style={{ flexBasis: "38%" }}>
          {/* LIVE FEED */}
          <div
            className="flex-1 rounded-md border border-rose-400/20 px-1.5 py-1 backdrop-blur"
            style={{
              background:
                "linear-gradient(160deg, rgba(159,18,57,0.22), rgba(15,23,42,0.7))",
            }}
          >
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-1 text-[5.5px] tracking-[0.18em] text-rose-300">
                <span className="h-1 w-1 rounded-full bg-rose-400 animate-pulse" />
                <span>LIVE FEED</span>
              </div>
              <span className="text-[5px] tabular-nums text-slate-400">02:14</span>
            </div>
            <div className="space-y-[3px]">
              {feed.map((f, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-1 rounded-sm px-1 py-[1.5px] ${f.hi ? "bg-rose-500/20 animate-pulse" : ""}`}
                >
                  <span className={`h-1 w-1 flex-shrink-0 rounded-full ${f.dot}`} />
                  <span className="flex-1 truncate text-[5.5px] text-slate-200">{f.text}</span>
                  <span className="text-[5px] tabular-nums text-slate-400">{f.time} ago</span>
                </div>
              ))}
            </div>
          </div>

          {/* CAMPAIGN STATS */}
          <div
            className="rounded-md border border-rose-400/20 px-1.5 py-1 backdrop-blur"
            style={{
              background:
                "linear-gradient(160deg, rgba(217,70,239,0.18), rgba(15,23,42,0.7))",
            }}
          >
            <div className="mb-1 flex items-center justify-between text-[5.5px] tracking-[0.18em] text-rose-300">
              <span>CAMPAIGN · Q2 GROWTH</span>
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <span className="text-[5.5px] text-slate-300">Reach</span>
                <div className="flex items-center gap-1">
                  <Spark up />
                  <span className="text-[6.5px] font-bold tabular-nums text-white">8.4M</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[5.5px] text-slate-300">Conversions</span>
                <div className="flex items-center gap-1">
                  <Spark />
                  <span className="text-[6.5px] font-bold tabular-nums text-emerald-400">+24%</span>
                </div>
              </div>
            </div>
            <div className="mt-1">
              <div className="mb-[1px] flex items-center justify-between text-[5px] tracking-wider text-rose-200/80">
                <span>GOAL</span>
                <span className="tabular-nums">78%</span>
              </div>
              <div className="h-1 w-full overflow-hidden rounded-full bg-slate-700/60">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: "78%",
                    background:
                      "linear-gradient(90deg, #f43f5e, #ec4899, #d946ef)",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────  Scene 6: BUSINESS REVENUE  ───────────── */
function RevenueScene() {
  const deals = [
    { name: "Helix Labs", tier: "Enterprise", amount: "₹40L", time: "2h ago", logo: "hex", color: "from-teal-400 to-emerald-500", initials: "HL", glow: true },
    { name: "Cobalt Studio", tier: "Series B", amount: "₹26L", time: "5h ago", logo: "tri", color: "from-indigo-400 to-blue-500", initials: "CS", glow: false },
    { name: "Tessera Health", tier: "Mid-Market", amount: "₹20L", time: "yesterday", logo: "rhomb", color: "from-amber-400 to-orange-500", initials: "TH", glow: false },
  ];

  const LogoMark = ({ type, color, initials }) => (
    <div className={`relative h-[14px] w-[14px] rounded-[3px] bg-gradient-to-br ${color} flex items-center justify-center shrink-0 shadow-[0_0_6px_rgba(16,185,129,0.15)]`}>
      <svg viewBox="0 0 14 14" className="absolute inset-0 h-full w-full opacity-90">
        {type === "hex" && (
          <polygon points="7,2.5 11,5 11,9 7,11.5 3,9 3,5" fill="none" stroke="white" strokeWidth="0.7" opacity="0.85" />
        )}
        {type === "tri" && (
          <>
            <polygon points="7,3 11,10 3,10" fill="none" stroke="white" strokeWidth="0.7" opacity="0.85" />
            <circle cx="7" cy="7.5" r="1.2" fill="white" opacity="0.7" />
          </>
        )}
        {type === "rhomb" && (
          <>
            <polygon points="7,2.5 11.5,7 7,11.5 2.5,7" fill="none" stroke="white" strokeWidth="0.7" opacity="0.85" />
            <line x1="2.5" y1="7" x2="11.5" y2="7" stroke="white" strokeWidth="0.5" opacity="0.6" />
          </>
        )}
      </svg>
    </div>
  );

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0f1e] font-mono text-white">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(0deg, rgba(16,185,129,0.6) 0 1px, transparent 1px 18px), repeating-linear-gradient(90deg, rgba(16,185,129,0.6) 0 1px, transparent 1px 18px)" }} />
      {/* Radial vignette */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 50% at 25% 20%, rgba(16,185,129,0.16), transparent 70%), radial-gradient(ellipse 50% 60% at 90% 100%, rgba(20,184,166,0.14), transparent 65%)" }} />
      {/* Floating dots */}
      <div className="absolute top-[8%] left-[55%] h-[3px] w-[3px] rounded-full bg-emerald-300/50 animate-ping" style={{ animationDuration: "3s" }} />
      <div className="absolute top-[40%] left-[88%] h-[2px] w-[2px] rounded-full bg-teal-300/60 animate-ping" style={{ animationDuration: "4s" }} />
      <div className="absolute top-[70%] left-[8%] h-[2px] w-[2px] rounded-full bg-lime-300/50 animate-ping" style={{ animationDuration: "3.5s" }} />
      <div className="absolute top-[20%] left-[72%] h-[2px] w-[2px] rounded-full bg-emerald-400/40 animate-ping" style={{ animationDuration: "5s" }} />

      <div className="relative h-full w-full p-2 flex flex-col gap-1.5">
        {/* TOP STRIP: Hero metric */}
        <div className="flex items-start justify-between gap-2 px-1 pt-0.5">
          <div className="flex flex-col">
            <span className="text-[5.5px] tracking-[0.18em] text-slate-400">MONTHLY RECURRING REVENUE</span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="text-[22px] font-bold leading-none tabular-nums text-emerald-200" style={{ textShadow: "0 0 12px rgba(110,231,183,0.35)" }}>₹2.05Cr</span>
            </div>
            <div className="flex items-center gap-1 mt-0.5 text-[6px]">
              <span className="text-emerald-300 font-semibold">↑ 24.8%</span>
              <span className="text-slate-500">MoM</span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-300 tabular-nums">2,438</span>
              <span className="text-slate-500">customers</span>
            </div>
          </div>
          {/* Sparkline */}
          <div className="flex flex-col items-end gap-0.5 mt-1">
            <svg viewBox="0 0 80 24" className="h-[24px] w-[80px]">
              <defs>
                <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="sparkLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#5eead4" />
                  <stop offset="100%" stopColor="#a3e635" />
                </linearGradient>
              </defs>
              <path d="M 0 18 L 8 16 L 16 17 L 24 14 L 32 15 L 40 11 L 48 12 L 56 8 L 64 9 L 72 5 L 80 3 L 80 24 L 0 24 Z" fill="url(#sparkFill)" />
              <polyline points="0,18 8,16 16,17 24,14 32,15 40,11 48,12 56,8 64,9 72,5 80,3" fill="none" stroke="url(#sparkLine)" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
              <circle cx="80" cy="3" r="1.6" fill="#a3e635" />
              <circle cx="80" cy="3" r="3" fill="#a3e635" opacity="0.3" className="animate-pulse" />
            </svg>
            <span className="text-[5px] text-slate-500 tracking-wider">30D TREND</span>
          </div>
        </div>

        {/* MIDDLE: Recent Closed Deals */}
        <div className="flex flex-col gap-1 mt-0.5">
          <div className="flex items-center justify-between px-0.5">
            <div className="flex items-center gap-1">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              <span className="text-[6px] tracking-[0.15em] text-slate-300 font-semibold">DEALS CLOSED · TODAY</span>
            </div>
            <span className="text-[5.5px] text-slate-500 tracking-wider">LIVE</span>
          </div>

          {deals.map((deal, i) => (
            <div
              key={i}
              className={`relative flex items-center gap-1.5 rounded-[4px] border border-emerald-400/15 bg-gradient-to-r ${deal.glow ? "from-emerald-500/[0.18] via-emerald-500/[0.08] to-emerald-500/[0.03] animate-pulse" : "from-white/[0.04] to-white/[0.01]"} px-1.5 py-1 backdrop-blur-sm`}
              style={deal.glow ? { animationDuration: "2.5s" } : {}}
            >
              <LogoMark type={deal.logo} color={deal.color} initials={deal.initials} />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-[7.5px] font-bold text-white leading-tight truncate">{deal.name}</span>
                <span className="text-[5.5px] text-slate-400 tracking-wide leading-tight">{deal.tier} · SaaS</span>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="text-[8px] font-bold tabular-nums text-emerald-300 leading-none">{deal.amount}<span className="text-slate-500 font-normal">/mo</span></span>
                <div className="flex items-center gap-1">
                  <span className="text-[5px] px-1 py-[1px] rounded-sm bg-emerald-500/20 ring-1 ring-emerald-400/40 text-emerald-200 tracking-wider">CLOSED ✓</span>
                  <span className="text-[5px] text-slate-500 tabular-nums">{deal.time}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* BOTTOM: KPI tiles + Persona */}
        <div className="flex gap-1.5 mt-auto">
          {/* KPI tiles (60%) */}
          <div className="flex gap-1 flex-[6]">
            {[
              { label: "ARR", value: "₹25Cr", delta: "↑ 18%", arrow: true },
              { label: "WIN RATE", value: "42%", delta: "↑ 3.2pt", arrow: false },
              { label: "AVG DEAL", value: "₹22L", delta: "↑ 8%", arrow: false },
            ].map((kpi, i) => (
              <div key={i} className="flex-1 flex flex-col items-start justify-between rounded-[3px] bg-emerald-500/10 ring-1 ring-emerald-400/20 px-1 py-1">
                <span className="text-[5px] tracking-[0.15em] text-slate-400">{kpi.label}</span>
                <span className="text-[9px] font-bold tabular-nums text-white leading-none mt-0.5">{kpi.value}</span>
                <span className="text-[5px] text-emerald-300 tabular-nums mt-0.5">{kpi.delta}</span>
              </div>
            ))}
          </div>

          {/* Persona card (38%) */}
          <div className="flex-[4] rounded-[3px] bg-gradient-to-br from-emerald-500/10 to-teal-500/5 ring-1 ring-emerald-400/20 px-1.5 py-1 flex items-center gap-1.5">
            {/* Avatar */}
            <div className="relative shrink-0">
              <svg viewBox="0 0 22 22" className="h-[22px] w-[22px]">
                <defs>
                  <linearGradient id="avatarBg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#5eead4" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
                <circle cx="11" cy="11" r="11" fill="url(#avatarBg)" />
                <circle cx="11" cy="8.5" r="3" fill="#0a0f1e" opacity="0.4" />
                <path d="M 4 22 Q 4 14 11 14 Q 18 14 18 22 Z" fill="#0a0f1e" opacity="0.4" />
                <text x="11" y="13" textAnchor="middle" fontSize="6" fontWeight="700" fill="white" fontFamily="monospace" opacity="0.95">PA</text>
              </svg>
              <span className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-400 ring-1 ring-[#0a0f1e]" />
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-[7px] font-bold text-white leading-tight truncate">Priya Anand</span>
              <span className="text-[5px] text-slate-400 leading-tight">VP Revenue</span>
              <div className="flex items-center gap-0.5 mt-0.5">
                <span className="relative flex h-1 w-1">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1 w-1 rounded-full bg-emerald-400" />
                </span>
                <span className="text-[4.5px] tracking-[0.15em] text-emerald-300">CLOSING NOW</span>
              </div>
              <span className="text-[5px] text-slate-400 mt-0.5 tabular-nums leading-tight">12 deals MTD · <span className="text-emerald-300">₹40Cr</span> booked</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top-right corner status */}
      <div className="absolute top-1 right-1.5 flex items-center gap-1">
        <span className="text-[5px] text-slate-500 tracking-[0.2em]">Q2 · FY26</span>
      </div>
    </div>
  );
}
const SCENES = [
  { id: 'apps', label: 'APP UI DESIGN', color: 'violet', tagline: 'Crafting interfaces users love', component: AppsScene },
  { id: 'web', label: 'WEB DEVELOPMENT', color: 'cyan', tagline: 'Fast, modern, SEO-ready websites', component: WebsScene },
  { id: 'drone', label: 'DRONE DEVELOPMENT', color: 'sky', tagline: 'Aerial systems engineered to spec', component: DroneScene },
  { id: 'iot', label: 'IoT PROJECTS', color: 'amber', tagline: 'Connected devices, end-to-end', component: IoTScene },
  { id: 'growth', label: 'SOCIAL & GROWTH', color: 'rose', tagline: 'Marketing that drives real reach', component: GrowthScene },
  { id: 'revenue', label: 'BUSINESS RESULTS', color: 'emerald', tagline: 'Measurable revenue growth', component: RevenueScene },
]

/* ---- inline service glyphs (kept tiny so 3×2 grid stays light) ---- */
function CodeGlyph({ className = 'size-2.5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6l-5 6 5 6M16 6l5 6-5 6" />
    </svg>
  )
}
function PhoneGlyph({ className = 'size-2.5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="6" y="2" width="12" height="20" rx="2.5" />
      <line x1="11" y1="18" x2="13" y2="18" />
    </svg>
  )
}
function ChipGlyph({ className = 'size-2.5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="1" />
      <rect x="9" y="9" width="6" height="6" rx="0.5" fill="currentColor" opacity="0.35" />
      <path d="M10 2v3M14 2v3M10 19v3M14 19v3M2 10h3M2 14h3M19 10h3M19 14h3" />
    </svg>
  )
}
function DroneGlyph({ className = 'size-2.5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="5" cy="5" r="2.4" fill="currentColor" opacity="0.4" />
      <circle cx="19" cy="5" r="2.4" fill="currentColor" opacity="0.4" />
      <circle cx="5" cy="19" r="2.4" fill="currentColor" opacity="0.4" />
      <circle cx="19" cy="19" r="2.4" fill="currentColor" opacity="0.4" />
      <line x1="6.5" y1="6.5" x2="10" y2="10" />
      <line x1="17.5" y1="6.5" x2="14" y2="10" />
      <line x1="6.5" y1="17.5" x2="10" y2="14" />
      <line x1="17.5" y1="17.5" x2="14" y2="14" />
      <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" opacity="0.75" />
    </svg>
  )
}
function TrendGlyph({ className = 'size-2.5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 17 9 11 13 15 21 7" />
      <polyline points="15 7 21 7 21 13" />
    </svg>
  )
}
function DollarGlyph({ className = 'size-2.5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2v20M16 6H10a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6H8" />
    </svg>
  )
}

/* ============================================================
   SERVICES — cinematic photo-card grid. Cards reveal on scroll
   ============================================================ */
function SvcCard({ svc, idx, revealed, hoveredIdx, setHoveredIdx }) {
  const inRow = idx % 4
  const dropDown = inRow === 1 || inRow === 3
  const baseTilt = ['-0.7deg', '0.6deg', '-0.5deg', '0.8deg'][inRow]
  const aspect = 'aspect-[4/5]'
  const isDimmed = hoveredIdx !== null && hoveredIdx !== idx

  return (
    <Link
      to={svc.to}
      className={`svc-card-overlay group block transition-all duration-500 ease-out ${dropDown ? 'lg:mt-24' : ''} ${isDimmed ? 'lg:opacity-40 lg:blur-[0.5px]' : ''}`}
      style={{
        animation: revealed ? `svc-reveal 0.95s cubic-bezier(0.22,1,0.36,1) ${idx * 110}ms both` : 'none',
        opacity: revealed ? undefined : 0,
      }}
      onMouseEnter={() => setHoveredIdx?.(idx)}
      onMouseLeave={() => setHoveredIdx?.(null)}
    >
      <div
        className={`relative ${aspect} overflow-hidden rounded-[20px] border border-white/[0.08] bg-white/[0.02] shadow-lg transition-all duration-300 group-hover:border-white/20 group-hover:shadow-[0_15px_30px_rgba(0,0,0,0.4)]`}
      >
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          {/* image with steady zoom on hover */}
          <img
            src={svc.image}
            alt={svc.title}
            loading="lazy"
            draggable="false"
            className="size-full select-none object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          />
        </div>

        {/* dark bottom gradient for title legibility (half gradient shadow) */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(2,13,35,0.92) 100%)' }} />

        {/* Steady color border on hover */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 size-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ overflow: 'visible' }}
        >
          <rect
            x="1.5" y="1.5"
            width="calc(100% - 3px)" height="calc(100% - 3px)"
            rx="27" ry="27"
            fill="none"
            stroke={`rgba(${svc.accent}, 0.85)`}
            strokeWidth="2"
            style={{ filter: `drop-shadow(0 0 4px rgba(${svc.accent}, 0.6))` }}
          />
        </svg>

        {/* title block overlaid at bottom-left */}
        <div className="absolute inset-x-3 bottom-3 sm:inset-x-5 sm:bottom-5">
          <h3 className="font-display text-[15px] sm:text-[22px] md:text-[22px] lg:text-[22px] xl:text-[26px] font-semibold leading-tight sm:leading-[1.05] tracking-tight text-white transition-transform duration-500 ease-out group-hover:-translate-y-1">
            {svc.title}
          </h3>
          <div className="hidden sm:flex mt-1.5 sm:mt-3 items-center gap-2 overflow-hidden">
            <span aria-hidden="true" className="block h-px w-0 bg-cyan-300 transition-all duration-500 ease-out group-hover:w-10" />
            <span className="-translate-x-2 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-cyan-300 opacity-0 transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:opacity-100">
              Explore →
            </span>
          </div>
        </div>
      </div>
    </Link>
  )
}

function ServicesSection() {
  const sectionRef = useRef(null)
  const [revealed, setRevealed] = useState(false)
  const [hoveredIdx, setHoveredIdx] = useState(null)

  useEffect(() => {
    const el = sectionRef.current
    if (!el || revealed) return
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(true)
      return
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true)
            obs.disconnect()
            break
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [revealed])

  /* Curated Unsplash stock — each photo themed to its service.
     auto=format keeps the format optimal per browser; fit=crop +
     w/h give us a portrait crop that matches the card aspect. */
  const services = [
    { title: 'Website Development', category: 'Engineering', accent: '56,189,248', image: homepageImg1, to: '/services/website-development' },
    { title: 'App Development', category: 'Engineering', accent: '168,85,247', image: homepageImg2, to: '/services/app-development' },
    { title: 'UI / UX Design', category: 'Design', accent: '232,121,249', image: homepageImg3, to: '/services/ui-ux-design' },
    { title: 'Cyber Security', category: 'Defense', accent: '244,114,182', image: homepageImg4, to: '/services/cyber-security' },
    { title: 'AI & Automation', category: 'Intelligence', accent: '52,211,153', image: homepageImg5, to: '/services/ai-automation' },
    { title: 'Drone Development', category: 'Aerospace', accent: '251,191,36', image: homepageImg6, to: '/services/drone-development' },
    { title: 'Software Development', category: 'Engineering', accent: '14,165,233', image: '/images/remote/unsplash-60064650ba.jpg', to: '/services/software-development' },
    { title: 'IoT Projects', category: 'Hardware', accent: '99,102,241', image: homepageImg8, to: '/services/iot-development' },
  ]

  const hoveredAccent = hoveredIdx !== null ? services[hoveredIdx].accent : '56,189,248'

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-ink-950 pt-12 pb-0 lg:pt-16 lg:pb-0" aria-label="Services">
      <style>{`
        @keyframes svc-reveal {
          0% { opacity: 0; transform: translateY(48px) scale(0.94); filter: blur(6px); }
          70% { filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes svc-heading-rise {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        /* Word-by-word headline reveal — each word slides up with a subtle
           anticipatory overshoot, creating a theatrical curtain-rise. */
        @keyframes svc-word-in {
          0% { opacity: 0; transform: translateY(80%) rotate(2deg); filter: blur(6px); }
          60% { opacity: 1; transform: translateY(-4%) rotate(0deg); filter: blur(0); }
          100% { opacity: 1; transform: translateY(0%) rotate(0deg); filter: blur(0); }
        }
        /* Ken Burns — slow continuous drift on idle photos. Four variants so
           the cards don't all move in sync. */
        @keyframes svc-kb-0 { from { transform: scale(1) translate(0%, 0%); } to { transform: scale(1.08) translate(-2%, 1.5%); } }
        @keyframes svc-kb-1 { from { transform: scale(1.07) translate(1.5%, -1.5%); } to { transform: scale(1) translate(0%, 0%); } }
        @keyframes svc-kb-2 { from { transform: scale(1) translate(-1%, -1%); } to { transform: scale(1.06) translate(2%, 2%); } }
        @keyframes svc-kb-3 { from { transform: scale(1.06) translate(2%, 1%); } to { transform: scale(1) translate(-1.5%, -1%); } }
        /* Aurora — three colored blobs drifting independently in the section
           backdrop, giving the whole canvas a slow atmospheric movement. */
        @keyframes svc-aurora-1 {
          0%, 100% { transform: translate3d(0%, 0%, 0) scale(1); }
          50% { transform: translate3d(18%, 12%, 0) scale(1.2); }
        }
        @keyframes svc-aurora-2 {
          0%, 100% { transform: translate3d(0%, 0%, 0) scale(1.1); }
          50% { transform: translate3d(-15%, -10%, 0) scale(0.9); }
        }
        @keyframes svc-aurora-3 {
          0%, 100% { transform: translate3d(0%, 0%, 0) scale(0.95); }
          33% { transform: translate3d(-18%, 14%, 0) scale(1.18); }
          66% { transform: translate3d(12%, -12%, 0) scale(1.05); }
        }
        /* Edge trace — glowing dash circumnavigates the card perimeter on hover */
        @keyframes svc-edge-trace {
          to { stroke-dashoffset: -680; }
        }
        /* Gleam sweep — diagonal sheen of light slides across the photo on hover */
        @keyframes svc-gleam-sweep {
          0% { transform: translateX(-110%) skewX(-12deg); opacity: 0; }
          25% { opacity: 1; }
          75% { opacity: 1; }
          100% { transform: translateX(110%) skewX(-12deg); opacity: 0; }
        }
      `}</style>

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Aurora — three slow-drifting colour blobs creating atmosphere */}
        <div className="absolute -top-1/4 -left-1/4 h-[70%] w-[70%] rounded-full blur-[140px] opacity-30 will-change-transform" style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.55), transparent 65%)', animation: 'svc-aurora-1 32s ease-in-out infinite' }} />
        <div className="absolute -bottom-1/4 -right-1/4 h-[65%] w-[65%] rounded-full blur-[150px] opacity-25 will-change-transform" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.55), transparent 65%)', animation: 'svc-aurora-2 38s ease-in-out infinite' }} />
        <div className="absolute top-1/3 left-1/3 h-[50%] w-[50%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px] opacity-20 will-change-transform" style={{ background: 'radial-gradient(circle, rgba(232,121,249,0.5), transparent 65%)', animation: 'svc-aurora-3 44s ease-in-out infinite' }} />
        {/* Accent spotlight — hue-shifts toward the hovered card's colour */}
        <div className="absolute inset-0 transition-[background] duration-700 ease-out" style={{ background: `radial-gradient(60% 45% at 50% 0%, rgba(${hoveredAccent},${hoveredIdx !== null ? 0.16 : 0.04}) 0%, transparent 60%)` }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header — title on left with cinematic word-by-word reveal,
            CTA button anchored to the right on lg+, stacked below on mobile */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
          <div className="max-w-3xl">
            {/* Below sm the size is tied to the viewport rather than fixed at
                40px: "Services we provide" measures ~9.6x the font size, so a
                flat 40px overflowed 382px of usable width and broke the title
                onto a third line. 8.2vw keeps both lines intact down to 320px,
                and the clamp ceiling means 40px still applies from ~490px up. */}
            <h2 className="font-display text-[clamp(1.5rem,8.2vw,2.5rem)] font-semibold leading-[1.05] tracking-tight text-white [&>span]:whitespace-nowrap sm:text-[52px] lg:text-[64px]">
              {(() => {
                const lines = [
                  ['Services', 'we', 'provide'],
                  ['at', 'Spectranox'],
                ]
                let wordIdx = 0
                return lines.map((line, li) => (
                  <span key={li} className="block">
                    {/* The separator is a real text node OUTSIDE the clip
                        wrapper. Inside it the space was cropped by
                        overflow:hidden and the words ran together; as a
                        margin it looked right but innerText, copy-paste and
                        screen readers still read "Servicesweprovide". */}
                    {line.flatMap((word, wi) => {
                      const i = wordIdx++
                      return [
                        <span key={wi} className="inline-block overflow-hidden pb-1 [vertical-align:bottom]">
                          <span
                            className="inline-block will-change-transform"
                            style={{
                              animation: revealed ? `svc-word-in 0.85s cubic-bezier(0.22,1.2,0.36,1) ${250 + i * 90}ms both` : 'none',
                              opacity: revealed ? undefined : 0,
                            }}
                          >
                            {/* "nox" picks up the logo blue, so the wordmark
                                reads the same here as it does in the navbar. */}
                            {word === 'Spectranox' ? (
                              <>
                                Spectra<span style={{ color: TITLE_BLUE }}>nox</span>
                              </>
                            ) : (
                              word
                            )}
                          </span>
                        </span>,
                        wi < line.length - 1 ? ' ' : null,
                      ]
                    })}
                  </span>
                ))
              })()}
            </h2>
            <p
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
              style={{ animation: revealed ? 'svc-heading-rise 0.8s cubic-bezier(0.22,1,0.36,1) 600ms both' : 'none', opacity: revealed ? undefined : 0 }}
            >
              Design, build, security and maintenance, all in-house. You deal
              with one team instead of stitching four vendors together.
            </p>
          </div>

          {/* View all services CTA — pinned right on lg+. Hidden below that:
              the same link already closes the section, and stacked on a phone
              this is the identical CTA twice within one scroll. */}
          <div
            className="hidden shrink-0 lg:block"
            style={{ animation: revealed ? 'svc-heading-rise 0.8s cubic-bezier(0.22,1,0.36,1) 700ms both' : 'none', opacity: revealed ? undefined : 0 }}
          >
            <Link
              to="/services"
              className="group/cta inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.05] px-4 py-2 sm:px-5 sm:py-2.5 font-sans text-xs font-semibold tracking-wide text-white backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:bg-cyan-400/10 hover:shadow-[0_0_20px_rgba(56,189,248,0.3)]"
            >
              <span aria-hidden="true" className="size-1.5 rounded-full bg-cyan-400 transition-transform duration-300 group-hover/cta:scale-125 shadow-[0_0_6px_#38bdf8]" />
              <span>View all services</span>
              <span aria-hidden="true" className="grid size-5 place-items-center rounded-full border border-white/20 transition-all duration-300 group-hover/cta:border-cyan-300/60 group-hover/cta:bg-cyan-300/20">
                <svg viewBox="0 0 24 24" className="h-3 w-3 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </span>
            </Link>
          </div>
        </div>

        {/* Cinematic editorial grid — magnetic tilt, parallax, overlay titles,
            inverted spotlight (hovered card stays bright while others dim),
            and a cursor-following EXPLORE badge over the whole grid. */}
        <div className="mt-6 lg:mt-8" style={{ perspective: '2400px' }}>
          <div
            className="relative grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4 lg:gap-x-7 lg:gap-y-0 lg:items-start"
          >
            {services.map((s, i) => (
              <SvcCard key={s.title} svc={s} idx={i} revealed={revealed} hoveredIdx={hoveredIdx} setHoveredIdx={setHoveredIdx} />
            ))}

          </div>
        </div>

        {/* Footer link */}
        <div className="mt-10 sm:mt-12 flex justify-center lg:mt-14">
          <Link
            to="/services"
            className="group/cta inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.05] px-4 py-2 sm:px-5 sm:py-2.5 font-sans text-xs font-semibold tracking-wide text-white backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:bg-cyan-400/10 hover:shadow-[0_0_20px_rgba(56,189,248,0.3)]"
          >
            <span aria-hidden="true" className="size-1.5 rounded-full bg-cyan-400 transition-transform duration-300 group-hover/cta:scale-125 shadow-[0_0_6px_#38bdf8]" />
            <span>See all services</span>
            <span aria-hidden="true" className="grid size-5 place-items-center rounded-full border border-white/20 transition-all duration-300 group-hover/cta:border-cyan-300/60 group-hover/cta:bg-cyan-300/20">
              <svg viewBox="0 0 24 24" className="h-3 w-3 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </section>
  )
}

/* ============================================================
   PhasesTimelineLight — white-theme editorial process timeline.
   Each phase = a numbered card on a horizontal connecting rail.
   Active gradient rail draws on view, traveling dot loops.
   Mobile collapses to a vertical timeline with the same cards.
   ============================================================ */
function PhasesTimelineLight({ phases }) {
  return (
    <div className="relative mt-14 lg:mt-20">
      <style>{`
        @keyframes pl-rail-draw { from { stroke-dashoffset: 1 } to { stroke-dashoffset: 0 } }
        @keyframes pl-dot-travel {
          0%   { left: 8%;  opacity: 0; }
          6%   { opacity: 1; }
          94%  { opacity: 1; }
          100% { left: 92%; opacity: 0; }
        }
        @keyframes pl-card-rise {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pl-station-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.18); }
        }
        @keyframes pl-ripple {
          0%   { transform: scale(0.6); opacity: 0.55; }
          80%  { opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>

      {/* DESKTOP — horizontal rail with stations + 5 cards */}
      <div className="relative hidden lg:block">
        {/* Connecting rail */}
        <div className="relative mb-8 h-12" aria-hidden="true">
          <svg viewBox="0 0 100 8" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible">
            <defs>
              <linearGradient id="pl-rail-grad" x1="0" x2="1">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="50%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#e879f9" />
              </linearGradient>
            </defs>
            {/* base slate line */}
            <line x1="8" y1="4" x2="92" y2="4" stroke="rgba(148,163,184,0.35)" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
            {/* gradient progress line — draws on view */}
            <line
              x1="8"
              y1="4"
              x2="92"
              y2="4"
              stroke="url(#pl-rail-grad)"
              strokeWidth="1.2"
              pathLength="1"
              strokeDasharray="1"
              strokeDashoffset="1"
              vectorEffect="non-scaling-stroke"
              style={{ animation: 'pl-rail-draw 2.2s cubic-bezier(0.65,0,0.35,1) 0.4s forwards' }}
            />
          </svg>

          {/* Station markers */}
          {phases.map((p, i) => {
            const step = phases.length > 1 ? 84 / (phases.length - 1) : 0
            const leftPct = 8 + i * step
            return (
              <div
                key={`st-${p.n}`}
                className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${leftPct}%` }}
              >
                {/* outer ripple */}
                <span
                  aria-hidden
                  className="absolute left-1/2 top-1/2 size-9 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    border: `1px solid ${p.accent}`,
                    animation: `pl-ripple 2.8s cubic-bezier(0.22,1,0.36,1) ${i * 0.45 + 1}s infinite`,
                  }}
                />
                {/* halo */}
                <span
                  aria-hidden
                  className="absolute left-1/2 top-1/2 size-7 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ background: p.accent, opacity: 0.18 }}
                />
                {/* white core ring */}
                <span
                  aria-hidden
                  className="absolute left-1/2 top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.08)]"
                />
                {/* accent core dot */}
                <span
                  aria-hidden
                  className="absolute left-1/2 top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    background: p.accent,
                    boxShadow: `0 0 10px ${p.accent}aa`,
                    animation: `pl-station-pulse 2.4s ease-in-out ${i * 0.3}s infinite`,
                  }}
                />
              </div>
            )
          })}

          {/* Traveler dot loops along the rail */}
          <span
            aria-hidden
            className="absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              background: 'linear-gradient(135deg, #38bdf8, #a855f7)',
              boxShadow: '0 0 14px rgba(168,85,247,0.7)',
              animation: 'pl-dot-travel 9s linear 2.6s infinite',
              left: '8%',
            }}
          />
        </div>

        {/* Phase cards */}
        <div className="grid gap-3 xl:gap-4" style={{ gridTemplateColumns: `repeat(${phases.length}, minmax(0, 1fr))` }}>
          {phases.map((p, i) => (
            <PhaseCardLight key={p.n} p={p} idx={i} />
          ))}
        </div>
      </div>

      {/* MOBILE — vertical rail */}
      <div className="relative lg:hidden">
        <div
          aria-hidden="true"
          className="absolute top-4 bottom-4 left-[15px] w-px bg-gradient-to-b from-cyan-300 via-violet-300 to-fuchsia-300"
        />
        <ol className="space-y-5">
          {phases.map((p, i) => (
            <li key={p.n} className="relative pl-12">
              <span
                aria-hidden
                className="absolute left-[5px] top-7 grid size-[22px] place-items-center rounded-full bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.08)]"
              >
                <span
                  className="size-2.5 rounded-full"
                  style={{ background: p.accent, boxShadow: `0 0 10px ${p.accent}aa` }}
                />
              </span>
              <PhaseCardLight p={p} idx={i} />
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

function PhaseCardLight({ p, idx }) {
  return (
    <article
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.10)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-18px_rgba(15,23,42,0.18)] lg:p-7"
      style={{ animation: `pl-card-rise 0.6s cubic-bezier(0.22,1,0.36,1) ${idx * 110}ms both` }}
    >
      {/* duration badge */}
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]"
          style={{ background: `${p.accent}15`, color: p.accent }}
        >
          <span className="size-1.5 rounded-full" style={{ background: p.accent }} />
          {p.duration}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
          Phase {p.n}
        </span>
      </div>

      {/* Big editorial phase number */}
      <span
        className="mt-5 font-display font-semibold leading-none tracking-tight"
        style={{
          fontSize: 'clamp(2.4rem, 4vw, 3rem)',
          letterSpacing: '-0.04em',
          background: `linear-gradient(135deg, ${p.accent}, ${p.accent}88)`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        {p.n}
      </span>

      {/* Title */}
      <h3 className="mt-4 font-display text-[22px] font-semibold leading-[1.15] tracking-tight text-slate-900 sm:text-2xl">
        {p.title}
      </h3>

      {/* Description */}
      <p className="mt-3 text-[13px] leading-[1.65] text-slate-500">{p.desc}</p>

      {/* Bottom accent line — gradient that scales in on hover */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 transition-transform duration-700 group-hover:scale-x-100"
        style={{ background: `linear-gradient(90deg, transparent, ${p.accent}, transparent)` }}
      />
    </article>
  )
}

/* ============================================================
   TrustedClientsSection — cinematic reveal on scroll into view.
   Photos slide in from the sides with scale + blur, the centre
   card rises with a gradient sheen sweep, and the language
   marquee fades in last. Triggered via useInView so it always
   plays the first time the section enters the viewport.
   ============================================================ */
function TrustedClientsSection() {
  // Real IntersectionObserver so the cinematic reveal plays the first time
  // the section actually scrolls into view (the shared useInView hook only
  // flips on mount which fires too early on long pages).
  const ref = useRef(null)
  const [revealed, setRevealed] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setRevealed(true)
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true)
          io.disconnect()
        }
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return (
    <section
      ref={ref}
      data-revealed={revealed ? 'true' : 'false'}
      className="trusted-clients main-container relative mt-0 sm:-mt-[18px] pt-0 pb-8 sm:pb-[50px] lg:mt-0 lg:pt-12"
      aria-labelledby="languages-heading"
    >
      <style>{`
        @keyframes tc-photo-in-left {
          0%   { opacity: 0; transform: translate3d(-80px, 30px, 0) scale(0.92); filter: blur(8px); }
          60%  { filter: blur(0); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
        }
        @keyframes tc-photo-in-right {
          0%   { opacity: 0; transform: translate3d(80px, 30px, 0) scale(0.92); filter: blur(8px); }
          60%  { filter: blur(0); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
        }
        @keyframes tc-card-rise {
          0%   { opacity: 0; transform: translateY(40px) scale(0.97); filter: blur(6px); }
          70%  { filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes tc-word-rise {
          0%   { opacity: 0; transform: translateY(120%) rotate(2deg); filter: blur(4px); }
          70%  { filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) rotate(0); filter: blur(0); }
        }
        @keyframes tc-marquee-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes tc-sheen-sweep {
          0%   { transform: translateX(-110%) skewX(-12deg); opacity: 0; }
          25%  { opacity: 1; }
          75%  { opacity: 1; }
          100% { transform: translateX(110%) skewX(-12deg); opacity: 0; }
        }
        @keyframes tc-glow-pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%      { opacity: 0.7; transform: scale(1.08); }
        }
        /* Initial pre-reveal state to avoid flash */
        .trusted-clients .tc-photo,
        .trusted-clients .tc-card,
        .trusted-clients .tc-eyebrow,
        .trusted-clients .tc-word,
        .trusted-clients .tc-marquee { opacity: 0; }
        .trusted-clients[data-revealed="true"] .tc-photo-l {
          animation: tc-photo-in-left 0.95s cubic-bezier(0.22,1,0.36,1) var(--d, 0s) both;
        }
        .trusted-clients[data-revealed="true"] .tc-photo-r {
          animation: tc-photo-in-right 0.95s cubic-bezier(0.22,1,0.36,1) var(--d, 0s) both;
        }
        .trusted-clients[data-revealed="true"] .tc-card {
          animation: tc-card-rise 1.05s cubic-bezier(0.22,1,0.36,1) 0.35s both;
        }
        .trusted-clients[data-revealed="true"] .tc-eyebrow {
          animation: tc-card-rise 0.8s cubic-bezier(0.22,1,0.36,1) 0.7s both;
        }
        .trusted-clients[data-revealed="true"] .tc-word {
          display: inline-block;
          animation: tc-word-rise 0.85s cubic-bezier(0.22,1,0.36,1) calc(0.85s + var(--w, 0s)) both;
        }
        .trusted-clients[data-revealed="true"] .tc-marquee {
          animation: tc-marquee-in 0.75s ease-out 1.2s both;
        }
        .trusted-clients[data-revealed="true"] .tc-sheen {
          animation: tc-sheen-sweep 1.6s cubic-bezier(0.22,1,0.36,1) 0.9s both;
        }
        .trusted-clients[data-revealed="true"] .tc-glow {
          animation: tc-glow-pulse 5s ease-in-out 1.2s infinite;
        }
      `}</style>

      {/* Ambient backdrop washes for cinematic depth */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="tc-glow absolute left-1/2 top-1/2 h-[420px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-200/25 blur-[140px]" />
        <div className="tc-glow absolute right-1/4 top-1/3 h-[280px] w-[420px] rounded-full bg-violet-200/20 blur-[120px]" style={{ animationDelay: '1.4s' }} />
      </div>

      <div
        className="flex flex-col-reverse items-stretch justify-center gap-y-8 overflow-hidden xl:flex-row xl:items-center xl:gap-x-8 xl:gap-y-0"
        role="region"
        aria-label="Use case examples"
      >
        {/* left area — 3-image collage */}
        <div className="flex w-full flex-row items-center justify-center overflow-hidden gap-x-2 sm:gap-x-3 h-[220px] sm:h-[280px] md:h-[327px] md:w-auto xl:shrink-0">
          <div className="flex h-full flex-1 flex-col items-center justify-center gap-y-2 sm:gap-y-3 md:flex-none">
            <div className="tc-photo tc-photo-l group relative overflow-hidden flex-1 min-h-0" style={{ '--d': '0.05s' }}>
              <figure className="h-full w-full overflow-hidden rounded-2xl sm:rounded-3xl md:rounded-[32px] border-[1.5px] border-black sm:w-[140px] md:w-[218px]">
                <img src="/images/police1.jpg" alt="Police & government clients" className="size-full object-cover transition-transform duration-500 group-hover:scale-106" />
              </figure>
            </div>
            <div className="tc-photo tc-photo-l group relative overflow-hidden flex-1 min-h-0" style={{ '--d': '0.2s' }}>
              <figure className="h-full w-full overflow-hidden rounded-2xl sm:rounded-3xl md:rounded-[32px] border-[1.5px] border-black sm:w-[140px] md:w-[218px]">
                <img src="/images/award.png" alt="Industry awards" className="size-full object-cover transition-transform duration-500 group-hover:scale-106" />
              </figure>
            </div>
          </div>
          <div className="tc-photo tc-photo-l group relative overflow-hidden h-full flex-1 md:flex-none" style={{ '--d': '0.35s' }}>
            <figure className="h-full w-full overflow-hidden rounded-2xl sm:rounded-3xl md:rounded-[32px] border-[1.5px] border-black sm:w-[140px] md:w-[218px]">
              <img src="/images/bank.png" alt="Banking & financial clients" className="size-full object-cover transition-transform duration-500 group-hover:scale-106" />
            </figure>
          </div>
        </div>

        {/* middle area — main card */}
        <div className="tc-card relative w-full max-w-[700px] space-y-8 overflow-hidden rounded-4xl border border-[#F4EFE7] bg-white/80 p-6 py-8 shadow-[0_18px_48px_-24px_rgba(15,23,42,0.15)] backdrop-blur xl:max-w-[600px]">
          {/* sheen sweep across the card */}
          <div aria-hidden className="tc-sheen pointer-events-none absolute inset-y-0 left-0 w-1/3" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)', filter: 'blur(10px)' }} />

          {/* Centred title block */}
          <div className="space-y-3 text-center">
            <p className="tc-eyebrow text-base sm:text-lg text-light font-normal">
              Delivered across four continents
            </p>
            <h3
              id="languages-heading"
              className="text-2xl sm:text-3xl lg:text-[34px] font-normal leading-tight"
            >
              <span className="inline-block overflow-hidden align-bottom pb-1">
                {['Trusted', 'by', 'Government,', 'Banking', '&', 'International', 'Clients.'].map((w, i) => (
                  <span key={`${w}-${i}`} className="tc-word mr-[0.22em]" style={{ '--w': `${i * 0.08}s` }}>
                    {w}
                  </span>
                ))}
              </span>
            </h3>
          </div>

          {/* Country chips marquee */}
          <div className="tc-marquee mask-fade-x overflow-hidden">
            <div
              className="flex w-max items-center animate-marquee"
              style={{ animationDuration: '22s' }}
            >
              {[0, 1].map((copy) => (
                <div
                  key={copy}
                  className="flex shrink-0 items-center gap-3 pr-3"
                  aria-hidden={copy === 1 ? 'true' : undefined}
                >
                  {COUNTRIES.map((l) => (
                    <div
                      key={`${copy}-${l.name}`}
                      className="flex h-12 shrink-0 items-center justify-start gap-x-3 rounded-full bg-ink-950 p-1 pr-4 shadow-[0_1px_2px_0_rgba(16,24,40,0.06)]"
                    >
                      <figure className="size-10 overflow-hidden rounded-full">
                        <img src={l.flag} alt={`${l.name} flag`} className="size-full object-cover" />
                      </figure>
                      <p className="whitespace-nowrap text-secondary font-normal">{l.name}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right area */}
        <div className="tc-photo tc-photo-r group relative hidden overflow-hidden xl:block xl:shrink-0" style={{ '--d': '0.5s' }}>
          <figure className="w-full max-w-[218px] overflow-hidden rounded-[32px] border-[1.5px] border-black">
            <img src="/images/reward.jpg" alt="Recognition & rewards" className="size-full object-cover transition-transform duration-500 group-hover:scale-106" />
          </figure>
        </div>
      </div>
    </section>
  )
}

export default function Home() {
  const [projectTab, setProjectTab] = useState('Websites')
  const [projectFilter, setProjectFilter] = useState('All')
  /* Seeded from the content snapshot baked at build time, not from a demo
     list. If the database is unreachable this is what the homepage shows —
     the real projects as of the last deploy, rather than six invented
     clients that no fetch failure would ever have cleared. */
  const [projectsData, setProjectsData] = useState(() => mapShowcase(fallbackContent.projects))

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE || '/api'
    let live = true
    fetchWithFallback(
      `${API_BASE}/get-showcase-projects.php`,
      (d) => (d?.success && Array.isArray(d.projects) && d.projects.length ? d.projects : null),
      'projects'
    ).then(({ data }) => {
      /* The fallback path returns rows too, so no branch here can leave the
         section showing something that was never real. */
      if (live && data?.length) setProjectsData(mapShowcase(data))
    })
    return () => { live = false }
  }, [])

  // Load the template's CSS + JS so the custom utility classes resolve and
  // Swiper/GSAP can initialise. Cleaned up when the home unmounts.
  useEffect(() => {
    const added = []

    const hadDark = document.documentElement.classList.contains('dark')
    /* Light theme: skip the dark class so legacy template uses light styles */

    CSS.forEach((href) => {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.dataset.legacy = '1'
      document.head.appendChild(link)
      added.push(link)
    })

    const style = document.createElement('style')
    style.dataset.legacy = '1'
    style.textContent = INLINE_CSS
    document.head.appendChild(style)
    added.push(style)

    window.scrollTo(0, 0)

    return () => {
      if (!hadDark) document.documentElement.classList.remove('dark')
      added.forEach((el) => el.remove())
    }
  }, [])

  return (
    <>
      <Seo />
      <main className="home-light standalone-page pt-20 overflow-x-hidden">
        {/* ============ HERO ============ */}
        <section className="relative isolate overflow-hidden bg-white pt-10 pb-16 sm:pt-12 lg:pt-16 lg:pb-20" aria-label="Hero section">
          <style>{`
            @keyframes hero-bg-drift {
              0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
              50%      { transform: translate3d(40px, -30px, 0) scale(1.08); }
            }
            @keyframes hero-bg-drift-rev {
              0%, 100% { transform: translate3d(0, 0, 0) scale(1.05); }
              50%      { transform: translate3d(-50px, 25px, 0) scale(0.96); }
            }
            @keyframes hero-aurora-pulse {
              0%, 100% { opacity: 0.55; }
              50%      { opacity: 0.9; }
            }
            @keyframes hero-orbit-spin {
              from { transform: rotate(0deg); }
              to   { transform: rotate(360deg); }
            }
            @keyframes hero-comet {
              0%   { transform: translateX(-20%) translateY(0); opacity: 0; }
              10%  { opacity: 1; }
              90%  { opacity: 1; }
              100% { transform: translateX(120%) translateY(60px); opacity: 0; }
            }
            @keyframes hero-twinkle {
              0%, 100% { opacity: 0.2; transform: scale(0.9); }
              50%      { opacity: 1;   transform: scale(1.15); }
            }
          `}</style>

          {/* === Layered LIGHT cinematic background — pastel aurora on white === */}

          {/* L0 — soft pastel gradient dome (sits BEHIND content, in front of -z-30) */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 100% 90% at 50% -10%, #e7f0ff 0%, #f6f5ff 35%, #ffffff 75%)',
            }}
          />

          {/* L1-L5 — the wide-canvas backdrop. Desktop only: every layer
              below is sized and positioned for a landscape viewport. */}
          <div aria-hidden className="pointer-events-none absolute inset-0 hidden lg:block">
            {/* L1 — drifting pastel aurora orbs (brand signature, light variants) */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-32 -right-20 h-[760px] w-[760px] rounded-full blur-[150px]"
              style={{
                background:
                  'radial-gradient(circle, rgba(56,189,248,0.55), rgba(56,189,248,0.15) 45%, transparent 70%)',
                animation: 'hero-bg-drift 22s ease-in-out infinite',
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-40 -left-40 h-[700px] w-[700px] rounded-full blur-[150px]"
              style={{
                background:
                  'radial-gradient(circle, rgba(0,142,255,0.20), rgba(0,142,255,0.06) 45%, transparent 70%)',
                animation: 'hero-bg-drift-rev 26s ease-in-out infinite',
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute top-1/3 left-[42%] h-[540px] w-[540px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[130px]"
              style={{
                background:
                  'radial-gradient(circle, rgba(125,211,252,0.18), rgba(125,211,252,0.05) 45%, transparent 70%)',
                animation: 'hero-aurora-pulse 7s ease-in-out infinite',
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute top-[12%] right-[14%] h-[320px] w-[320px] rounded-full blur-[110px]"
              style={{
                background:
                  'radial-gradient(circle, rgba(0,142,255,0.24), rgba(0,142,255,0.06) 50%, transparent 75%)',
                animation: 'hero-aurora-pulse 9s ease-in-out 1.2s infinite',
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-[14%] right-[28%] h-[260px] w-[260px] rounded-full blur-[100px]"
              style={{
                background:
                  'radial-gradient(circle, rgba(148,163,184,0.16), rgba(148,163,184,0.04) 50%, transparent 75%)',
                animation: 'hero-aurora-pulse 11s ease-in-out 2.6s infinite',
              }}
            />

            {/* L2 — engineered grid texture in pastel blue (faded toward center) */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(59,130,246,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.10) 1px, transparent 1px)',
                backgroundSize: '60px 60px',
                maskImage:
                  'radial-gradient(ellipse 80% 75% at 50% 50%, #000 25%, transparent 85%)',
                WebkitMaskImage:
                  'radial-gradient(ellipse 80% 75% at 50% 50%, #000 25%, transparent 85%)',
              }}
            />

            {/* L3 — fully-visible thin circles scattered as decorative ornaments */}
            {/* Top-right cluster: 3 concentric circles, fully contained */}
            <div
              aria-hidden
              className="pointer-events-none absolute right-[6%] top-[10%] h-[260px] w-[260px]"
              style={{ animation: 'hero-orbit-spin 90s linear infinite' }}
            >
              <div className="absolute inset-0 rounded-full border border-azure-400/25" />
              <div className="absolute inset-[18%] rounded-full border border-slate-300/40" />
              <div className="absolute inset-[36%] rounded-full border border-azure-400/15" />
              <span className="absolute -top-1 left-1/2 size-2 -translate-x-1/2 rounded-full bg-cyan-400 shadow-[0_0_14px_rgba(56,189,248,0.9)]" />
              <span className="absolute top-1/2 -right-1 size-1.5 -translate-y-1/2 rounded-full bg-violet-400 shadow-[0_0_12px_rgba(167,139,250,0.9)]" />
            </div>

            {/* Mid-left smaller cluster: 2 circles */}
            <div
              aria-hidden
              className="pointer-events-none absolute left-[4%] top-[38%] h-[180px] w-[180px]"
              style={{ animation: 'hero-orbit-spin 110s linear reverse infinite' }}
            >
              <div className="absolute inset-0 rounded-full border border-slate-300/40" />
              <div className="absolute inset-[24%] rounded-full border border-azure-400/15" />
              <span className="absolute top-[8%] right-[12%] size-1.5 rounded-full bg-violet-400 shadow-[0_0_12px_rgba(167,139,250,0.9)]" />
            </div>

            {/* Bottom-center tiny ornament */}
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-[8%] left-[42%] h-[120px] w-[120px]"
              style={{ animation: 'hero-orbit-spin 70s linear infinite' }}
            >
              <div className="absolute inset-0 rounded-full border border-cyan-400/30" />
              <div className="absolute inset-[30%] rounded-full border border-fuchsia-400/25" />
              <span className="absolute -top-1 left-1/2 size-1.5 -translate-x-1/2 rounded-full bg-fuchsia-400 shadow-[0_0_10px_rgba(232,121,249,0.9)]" />
            </div>

            {/* L4 — colored constellation dots (replaces white twinkles for visibility on light) */}
            {[
              { top: '12%', left: '8%', size: 4, delay: '0s', color: '#38bdf8' },
              { top: '22%', left: '34%', size: 3, delay: '1.2s', color: '#a855f7' },
              { top: '8%', left: '62%', size: 5, delay: '2.4s', color: '#e879f9' },
              { top: '40%', left: '14%', size: 3, delay: '0.6s', color: '#38bdf8' },
              { top: '55%', left: '46%', size: 4, delay: '3s', color: '#0ea5e9' },
              { top: '72%', left: '78%', size: 3, delay: '1.8s', color: '#a855f7' },
              { top: '30%', left: '88%', size: 4, delay: '2.2s', color: '#e879f9' },
              { top: '64%', left: '24%', size: 4, delay: '0.4s', color: '#38bdf8' },
              { top: '18%', left: '74%', size: 3, delay: '3.4s', color: '#a855f7' },
              { top: '82%', left: '12%', size: 3, delay: '2.8s', color: '#e879f9' },
              { top: '48%', left: '6%', size: 4, delay: '1.5s', color: '#0ea5e9' },
              { top: '88%', left: '58%', size: 3, delay: '0.9s', color: '#a855f7' },
            ].map((s, i) => (
              <span
                key={i}
                aria-hidden
                className="pointer-events-none absolute rounded-full"
                style={{
                  top: s.top,
                  left: s.left,
                  width: s.size,
                  height: s.size,
                  background: s.color,
                  boxShadow: `0 0 10px ${s.color}aa`,
                  animation: `hero-twinkle 4s ease-in-out ${s.delay} infinite`,
                }}
              />
            ))}

            {/* L5 — sweeping color comets across the hero */}
            <span
              aria-hidden
              className="pointer-events-none absolute top-[18%] left-0 h-[2px] w-40"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(56,189,248,0.9), transparent)',
                filter: 'blur(0.6px)',
                animation: 'hero-comet 12s ease-in-out 3s infinite',
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute top-[64%] left-0 h-[2px] w-32"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(168,85,247,0.85), transparent)',
                filter: 'blur(0.6px)',
                animation: 'hero-comet 14s ease-in-out 7s infinite',
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute top-[40%] left-0 h-[2px] w-28"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(232,121,249,0.85), transparent)',
                filter: 'blur(0.6px)',
                animation: 'hero-comet 16s ease-in-out 10s infinite',
              }}
            />
          </div>

          {/* ===== Phone and tablet backdrop (lg:hidden) =====
              Same palette, built for a tall narrow frame: one glow behind the
              headline, one for depth low-left, two calm arcs for structure, and
              a grid that fades out before it reaches the copy. */}
          <div aria-hidden className="pointer-events-none absolute inset-0 lg:hidden">
            {/* Vertical wash — colour concentrated at the top, clean white by
                the time it reaches the CTAs and the device cluster. */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, #d8e9ff 0%, #e8f2ff 30%, #f5faff 62%, #ffffff 100%)',
              }}
            />

            {/* Halo behind the headline. */}
            <div
              className="absolute left-1/2 top-[-16%] h-[440px] w-[440px] -translate-x-1/2 rounded-full blur-[90px]"
              style={{
                background:
                  'radial-gradient(circle, rgba(0,142,255,0.30), rgba(0,142,255,0.09) 46%, transparent 72%)',
                animation: 'hero-aurora-pulse 8s ease-in-out infinite',
              }}
            />

            {/* Low-left glow for depth. */}
            <div
              className="absolute -left-28 top-[44%] h-[320px] w-[320px] rounded-full blur-[85px]"
              style={{
                background:
                  'radial-gradient(circle, rgba(125,211,252,0.42), rgba(125,211,252,0.10) 50%, transparent 74%)',
                animation: 'hero-bg-drift-rev 26s ease-in-out infinite',
              }}
            />

            {/* Two concentric arcs, drawn to the viewport rather than scattered,
                so they frame the headline instead of crossing it. */}
            <svg
              className="absolute inset-x-0 top-[-9%] h-[300px] w-full"
              viewBox="0 0 400 300"
              fill="none"
              preserveAspectRatio="none"
            >
              <ellipse cx="200" cy="150" rx="190" ry="140" stroke="rgba(0,142,255,0.16)" strokeWidth="1" />
              <ellipse cx="200" cy="150" rx="142" ry="104" stroke="rgba(0,142,255,0.10)" strokeWidth="1" />
            </svg>

            {/* Grid at half the desktop pitch and a third the strength, masked
                away before it can sit behind body copy. */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(0,142,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(0,142,255,0.07) 1px, transparent 1px)',
                backgroundSize: '32px 32px',
                maskImage: 'linear-gradient(180deg, #000 0%, transparent 58%)',
                WebkitMaskImage: 'linear-gradient(180deg, #000 0%, transparent 58%)',
              }}
            />

            {/* Single slow highlight sweeping the top third. */}
            <span
              className="absolute left-0 top-[22%] h-[2px] w-32"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(0,142,255,0.55), transparent)',
                filter: 'blur(0.6px)',
                animation: 'hero-comet 15s ease-in-out 4s infinite',
              }}
            />
          </div>

          {/* L6 — bottom edge gradient hairline for clean separation */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(56,189,248,0.45), rgba(168,85,247,0.55), rgba(232,121,249,0.45), transparent)',
            }}
          />

          <div className="main-container relative z-10">
            <div className="grid grid-cols-1 items-center gap-12 xl:grid-cols-[0.92fr_1.24fr] xl:gap-8">
              {/* ---- LEFT: text ---- */}
              <div className="text-center xl:text-left">
                <h1
                  data-ns-animate=""
                  data-delay="0.2"
                  className="font-display font-bold leading-[1.08] tracking-tight text-white text-[clamp(1.75rem,7.4vw,2.25rem)] sm:text-[clamp(2.25rem,4.6vw,3.5rem)]"
                >
                  {/* One clause per line, none of them allowed to wrap: the
                      three-beat rhythm is the point of the headline and a
                      mid-clause break would lose it. */}
                  <span className="block whitespace-nowrap">Powerful Software.</span>
                  <span className="block whitespace-nowrap">Smarter Business.</span>
                  {/* Inline colour, not a class: `.home-light h1` forces every
                      heading to slate-900 with !important, but it targets the
                      h1 itself, so a colour set on the span still wins. */}
                  <span className="block whitespace-nowrap" style={{ color: TITLE_BLUE }}>Real Results.</span>
                </h1>

              <MobileExpandableDescription className="mx-auto mt-6 max-w-[520px] text-base leading-relaxed text-slate-400 sm:text-lg xl:mx-0">
                From idea to execution, we build custom websites, web apps, AI tools,
                IoT systems and secure platforms that help ambitious businesses grow
                faster and scale smarter.
              </MobileExpandableDescription>

                {/* capability row */}
                <ul
                  data-ns-animate=""
                  data-delay="0.35"
                  className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 xl:grid xl:grid-cols-2 xl:justify-start xl:gap-x-6"
                >
                  {HERO_CAPABILITIES.map(({ label, Icon }) => (
                    <li key={label} className="flex items-center gap-2 text-[13px] font-medium text-slate-300 sm:text-sm">
                      <Icon className="h-[18px] w-[18px] shrink-0 text-[#1d4ed8]" strokeWidth={2} />
                      {label}
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div
                  data-ns-animate=""
                  data-delay="0.4"
                  className="mt-8 flex flex-row flex-wrap items-center justify-center gap-2.5 sm:gap-3 xl:justify-start"
                >
                  <Link
                    to="/contact"
                    className="btn-on-solid group inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1d4ed8] px-5 py-2.5 text-[14px] font-bold text-white sm:gap-2 sm:px-8 sm:py-4 sm:text-[17px] shadow-[0_16px_34px_-12px_rgba(29,78,216,0.75)] transition-all hover:-translate-y-0.5 hover:bg-[#1e40af] hover:shadow-[0_20px_40px_-12px_rgba(29,78,216,0.9)]"
                  >
                    Talk to us
                    <svg viewBox="0 0 24 24" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </Link>
                  <Link
                    to="/portfolio"
                    className="inline-flex items-center justify-center rounded-full border-2 px-5 py-2 text-[14px] font-semibold transition-all hover:-translate-y-0.5 sm:px-7 sm:py-3 sm:text-base"
                    style={{ borderColor: 'rgba(15,23,42,0.18)', color: '#0f172a', background: 'transparent' }}
                  >
                    See our work
                  </Link>
                </div>

                {/* Who you actually deal with.

                    This row used to read "Trusted by 20+ / Businesses
                    Worldwide" over four stock portraits — the same client
                    count the section below states, next to photographs of
                    people who are not our customers. It says something the
                    rest of the page does not instead: who picks up when you
                    write in, and how fast. */}
                <div
                  data-ns-animate=""
                  data-delay="0.5"
                  className="mt-9 flex items-center justify-center gap-4 xl:justify-start"
                >
                  <div className="flex -space-x-3">
                    {FOUNDERS.map((f) => (
                      <img
                        key={f.name}
                        className="size-12 rounded-full object-cover object-top ring-2 ring-white"
                        src={f.img}
                        alt={`${f.name}, ${f.role}`}
                        loading="lazy"
                      />
                    ))}
                    <span className="inline-flex size-12 items-center justify-center rounded-full bg-[#1d4ed8] ring-2 ring-white" aria-hidden="true">
                      <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 5h16v12H7l-3 3V5z" />
                      </svg>
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="text-base font-semibold text-white">Talk to the founders, not a sales desk</p>
                    <p className="text-sm text-slate-400">Scoping call is free · we reply within a day</p>
                  </div>
                </div>
              </div>

              {/* ---- RIGHT: analytics dashboard + overlapping cards ----
                  Replaces the three stacked product shots. Sizing lives in
                  the component: it scales off its own container width, not
                  the viewport, because this column stops growing once the
                  page container hits max-w-7xl. */}
              <AnalyticsHeroVisual />
            </div>
          </div>
        </section>

        {/* ============ EXPERIENCE ============ */}
        <ExperienceSection />

        {/* ============ STATS / SUCCESS ============ */}
        {/* Hidden for now — remove the `hidden` class to bring it back. */}
        <section data-ns-animate="" data-delay="0.1" className="hidden bg-ink-950 py-8 sm:py-10 lg:py-12" aria-label="Success Metrics">
          <div className="main-container">
            <div className="overflow-hidden rounded-[20px] bg-ink-950 border border-white/10 shadow-sm">
              <div className="grid grid-cols-2 lg:grid-cols-4">
                {[
                  { value: 60, suffix: '%', label: 'Projects Completed', accent: '#dcf75e', icon: 'check' },
                  { value: 25, suffix: '+', label: 'Team Members', accent: '#67e8f9', icon: 'users' },
                  { value: 250, suffix: '+', label: 'Satisfied Clients', accent: '#fb9a8f', icon: 'like' },
                  { value: 5, suffix: '+', label: 'Countries Served', accent: '#a78bfa', icon: 'globe' },
                ].map((s, i) => (
                  <div
                    key={s.label}
                    className={[
                      'group relative flex flex-col items-center justify-center gap-1.5 px-2 py-4 sm:gap-2 sm:py-4 border-white/10',
                      // Mobile (2 cols): cells 1,3 get left border; cells 2,3 get top border
                      i % 2 === 1 ? 'border-l' : '',
                      i >= 2 ? 'border-t' : '',
                      // Desktop (4 cols): only vertical dividers, no horizontal
                      'lg:border-t-0',
                      i === 0 ? 'lg:border-l-0' : 'lg:border-l',
                    ].filter(Boolean).join(' ')}
                  >
                    {/* icon pill (oval, matches original chassis) — half size */}
                    <div
                      className="grid place-items-center rounded-full px-3 py-1.5 transition-transform duration-500 group-hover:scale-110"
                      style={{ background: s.accent, boxShadow: `0 3px 8px -2px ${s.accent}66` }}
                    >
                      <StatIcon type={s.icon} size={11} />
                    </div>

                    {/* number + label — half size */}
                    <div className="text-center">
                      <h3
                        className="font-extrabold leading-none tabular-nums tracking-tight text-white text-[16px] sm:text-[20px] lg:text-[24px]"
                        style={{ textShadow: `0 0 12px ${s.accent}40` }}
                      >
                        <Counter value={s.value} suffix={s.suffix} duration={2200} />
                      </h3>
                      <p className="mt-1 text-[10px] font-semibold tracking-wide text-white/80 sm:mt-1 sm:text-xs">
                        {s.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ USE-CASE CARDS + TRUSTED BY ============ */}
        <TrustedClientsSection />


        {/* ============ CAPABILITIES — interactive services index ============ */}
        <ServicesSection />

        {/* ============ SELECTED WORK (PROJECT SHOWCASE) ============ */}
        <section className="relative overflow-hidden bg-ink-950 pt-[90px] pb-10 sm:pb-14 lg:pb-16">
          {/* ambient backdrop */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-32 left-1/3 h-[420px] w-[420px] rounded-full bg-[#38bdf8]/[0.05] blur-[120px]" />
            <div className="absolute bottom-0 right-0 h-[360px] w-[360px] rounded-full bg-[#67e8f9]/[0.04] blur-[120px]" />
          </div>

          <div className="relative mx-auto w-full max-w-[1240px] px-5 sm:px-8">
            {/* heading row — title left, filter pills right (replaces Start-a-project button) */}
            <div className="mb-10 flex flex-col items-start gap-6 md:mb-12 md:flex-row md:items-end md:justify-between w-full max-w-full overflow-hidden">
              <div className="max-w-2xl">
                <h2 className="font-display text-[34px] leading-[1.05] tracking-tight text-white sm:text-[44px] lg:text-[52px]">
                  Our <span className="text-slate-400">featured projects</span>
                </h2>
              </div>
              <div className="flex w-full max-w-full overflow-x-auto whitespace-nowrap gap-2 self-start pb-3 md:w-auto md:flex-wrap md:overflow-visible md:pb-0 md:justify-end md:self-auto scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {PROJECT_FILTER_TABS.map((tab) => {
                  const isActive = tab === projectFilter
                  const count = tab === 'All' 
                    ? projectsData.length 
                    : projectsData.filter((p) => {
                        if (!p.category) return false
                        const cats = p.category.split(',').map((c) => c.trim().toLowerCase())
                        return cats.includes(tab.toLowerCase())
                      }).length
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setProjectFilter(tab)}
                      className={`group/pill inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-[13px] font-medium transition-all duration-200 ${isActive
                          ? 'border-slate-900 bg-slate-900 text-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.5)]'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                    >
                      {tab}
                      <span className={`font-mono text-[11px] ${isActive ? 'text-white/70' : 'text-slate-400'}`}>
                        {String(count).padStart(2, '0')}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <ProjectFilters active={projectFilter} setActive={setProjectFilter} projects={projectsData} />
          </div>
        </section>

        {/* ============ ADLC LIFECYCLE + COST ============ */}
        <AdlcCostBand />

        {/* ============ VALUE CAROUSEL — 7 reasons interactive ============ */}
        <ValueCarousel />

        {/* ============ 24/7 SUPPORT — always-on callout ============ */}
        <section className="relative overflow-hidden bg-ink-950 py-10 sm:py-14 lg:py-16" aria-label="Always-on 24/7 support">
          {/* ambient backdrop */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-24 right-0 h-[420px] w-[520px] rounded-full bg-[#38bdf8]/[0.07] blur-[140px]" />
            <div className="absolute bottom-0 left-0 h-[360px] w-[420px] rounded-full bg-[#a855f7]/[0.06] blur-[120px]" />
          </div>

          <div className="relative mx-auto w-full max-w-[1240px] px-5 sm:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
              {/* LEFT — 24/7 support illustration. Leads the stack on phones. */}
              <div className="relative order-1 lg:order-1">
                <img
                  src="/images/remote/unsplash-68187d10d6.jpg"
                  alt="Round-the-clock support engineers"
                  loading="lazy"
                  draggable="false"
                  className="block w-full rounded-3xl border border-white/15 object-cover aspect-[4/3] shadow-[0_30px_80px_-30px_rgba(56,189,248,0.35),0_0_0_1px_rgba(255,255,255,0.04)_inset]"
                />
              </div>

              {/* RIGHT — stat + title + description + CTA (24/7 support) */}
              <div className="relative order-2 lg:order-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-ink-950 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 shadow-sm">
                  <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-[#34d399]" />
                  Always-on support
                </div>
                <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span
                    className="font-display text-[clamp(2.75rem,6vw,4.5rem)] font-extrabold leading-none tracking-tight"
                    style={{ background: 'linear-gradient(135deg, #38bdf8, #a855f7)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent', textShadow: '0 0 60px rgba(56,189,248,0.35)' }}
                  >
                    24/7
                  </span>
                  <span className="font-sans text-[clamp(1.125rem,2.2vw,1.75rem)] font-semibold tracking-tight text-white">
                    Support Coverage
                  </span>
                </div>
                <h2 className="mt-3 font-display text-[30px] leading-[1.1] tracking-tight text-white sm:text-[38px] lg:text-[42px]">
                  We stay with you long after launch.
                </h2>
                <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-slate-300 sm:text-base">
                  You get <strong className="text-white">real engineers</strong>, on call, at any hour. Maintenance, revisions, a quick strategy call when you need one. Nobody files a ticket and waits three days. We keep working on it with you long after the project deployment.
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to="/contact"
                    className="btn-on-solid inline-flex items-center gap-2 rounded-full bg-[#1d4ed8] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5 hover:bg-[#1e40af] hover:shadow-blue-600/50"
                  >
                    Talk to us
                    <span aria-hidden>→</span>
                  </Link>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-semibold text-white transition-all hover:border-white/30 hover:bg-white/[0.06]"
                  >
                    How we work
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ TESTIMONIALS — Orbit Theater ============ */}
        <ReviewsSection />

        {/* ============ CONTACT FORM ============ */}
        <ContactSection />
      </main>
    </>
  )
}
